import { useState, useCallback, useEffect, useLayoutEffect, useRef, useReducer, lazy, startTransition, Suspense, type ReactNode } from "react";
import KeyboardDiagModalV2 from "@/components/KeyboardDiagModalV2";
import ChatHeader from "@/components/ChatHeader";
import ConversationsDrawer, { type DrawerConversation } from "@/components/ConversationsDrawer";
import { deleteConversation } from "@/lib/deleteConversation";
import { MessagesSquare } from "lucide-react";
import WelcomeScreen from "@/components/WelcomeScreen";
import ChatMessages, { type Message, type LearnReceipt, type ChatMessagesHandle } from "@/components/ChatMessages";
import ChatInput from "@/components/ChatInput";
import { toast } from "sonner";
import type { ImageResult } from "@/components/ImageGrid";

import { useMessageLimit } from "@/hooks/useMessageLimit";
import BottomNavBar, { type TabId } from "@/components/BottomNavBar";

// Code-split: each tab and tool modal loads its own chunk on first use instead
// of shipping in the main bundle. Chat stays static — it's the default tab.
const RenoBudgetTracker = lazy(() => import("@/components/RenoBudgetTracker"));
const GiftTracker = lazy(() => import("@/components/GiftTracker"));
const ProteinTracker = lazy(() => import("@/components/ProteinTracker"));
const PartyPlannerModal = lazy(() => import("@/components/PartyPlanner/PartyPlannerModal"));
const PartyHub = lazy(() => import("@/components/PartyPlanner/PartyHub"));
const PartyPickerGate = lazy(() => import("@/components/PartyPlanner/PartyPickerGate"));
const GardenPlannerTool = lazy(() => import("@/components/GardenPlannerTool"));
const SummerPlannerTool = lazy(() => import("@/components/SummerPlannerTool"));
const TripPlannerTool = lazy(() => import("@/components/TripPlannerTool"));
const RoutinesTool = lazy(() => import("@/components/RoutinesTool"));
const ToolsTab = lazy(() => import("@/components/ToolsTab"));
const MyKinTab = lazy(() => import("@/components/MyKinTab"));
const TodayTab = lazy(() => import("@/components/TodayTab"));
const TasksTab = lazy(() => import("@/components/TasksTab"));

/** Minimal on-brand fallback shown while a lazy tab chunk loads. Fills the
 *  tab area on Ghost White so there's no layout shift, spinner in brand purple. */
// TabLoader moved to its own file Aug 21 — it now delays its spinner 180ms
// so a pre-warmed lazy chunk's 1-2 frame suspension paints nothing at all.
/** Defers mounting a lazy tool modal until the first time it's opened, then
 *  keeps it mounted (matching the previous always-mounted behavior, where each
 *  modal returns null while closed). Fallback is null: the modal simply
 *  appears once its chunk finishes loading — no layout shift. */
const LazyMount = ({ open, children }: { open: boolean; children: ReactNode }) => {
  const openedOnceRef = useRef(false);
  if (open) openedOnceRef.current = true;
  if (!openedOnceRef.current) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};
import { useFeature } from "@/lib/features";
import { MODAL_TAGS, stripModalTags } from "@/lib/modalTags";
// #1900 PR 3 — the chat hand-off. Parser and stripper live in _shared/routines.ts
// because ChatMessages.tsx and the SMS path need the same answer; see the header
// there for why there is only one copy of the regex.
import {
  parseRoutineDraft,
  stripRoutineDraft,
  stripTrailingPartialMarker,
  type RoutineHandoff,
} from "../../supabase/functions/_shared/routines";
import { reportError } from "@/lib/reportError";
import { isAuthNetworkError } from "@/lib/authNetworkError";
import { isTransportFailureMessage } from "@/lib/transportFailure";
import {
  createConversationWithRetry,
  TRANSPORT_STATUS,
} from "@/lib/createConversationWithRetry";
import { saveMessageWithRetry } from "@/lib/saveMessageWithRetry";
import {
  readConversationMessages,
  attachIdToTrailingAssistant,
  clearMessageId,
} from "@/lib/chatMessageIdentity";
import SavedToast from "@/components/SavedToast";
import MomTypeSelector from "@/components/MomTypeSelector";
import WelcomeOnboarding from "@/components/WelcomeOnboarding";
import KidsCrewSetup from "@/components/KidsCrewSetup";
import NameCaptureModal from "@/components/NameCaptureModal";
import { getFirstName } from "@/lib/userDisplayName";
import { useMomTypes } from "@/hooks/useMomTypes";
import { useAuth } from "@/hooks/useAuth";
import { usePreferences } from "@/hooks/useUserPreferences";
import { useAdmin } from "@/hooks/useAdmin";
import { useIsPro } from "@/hooks/useIsPro";
import { useRealtimeVoice } from "@/hooks/useRealtimeVoice";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { useStableKeyboardOffset } from "@/hooks/useStableKeyboardOffset";
import KBDebugStrip from "@/components/KBDebugStrip";
import TabLoader from "@/components/TabLoader";
import { releaseVvHeightOnRawClose } from "@/lib/openSheetRegistry";
import { useStatusBar } from "@/hooks/useStatusBar";
import { supabase } from "@/integrations/supabase/client";
import MealPlanCommitBar from "@/components/MealPlanCommitBar";
import { activeMealPlanRunFromMessages, reduceMealPlanCard, MEAL_PLAN_CARD_COPY, type MealPlanCardState } from "@/lib/mealPlanChatCard";
import { assertWriteLanded } from "@/lib/scheduledTaskEdit";
import { parseKinToolEvent, type KinToolEvent } from "@/lib/kinToolTags";
import { groceryReceiptLine, groceryUndoPlan } from "@/lib/groceryReceipt";
import { naturalPhrase } from "@/lib/familyFactPhrase";
import { undoFamilyFact } from "@/lib/rememberFamilyFact";
import { showSaveFailedToast } from "@/lib/saveFailureToast";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";
import { requestChatTitle } from "@/lib/chatTitle";
import { REMINDER_NUDGE_INTERVAL_DAYS, REMINDER_NUDGE_TOAST_SUFFIX } from "@/lib/reminderNudgeRepeat";
import { SIGNUP_CHOICE_PROMPT, SIGNUP_CONFIRM_PROMPT, SIGNUP_FILING, SIGNUP_UNREADABLE } from "../../supabase/functions/_shared/signupSheet";
import { shouldShowFirstEmailToast } from "@/lib/firstEmailToast";
import { startSessionForUser, trackFeature } from "@/lib/analytics";

const CHAT_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-chat`;
const IMAGE_SEARCH_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/kin-image-search`;

const VISUAL_INTENT = /\b(inspir|inspo|mood\s*board|what\s+does\s+\w+\s+look\s+like|what\s+do\s+\w+\s+look\s+like)\b/i;
const VISUAL_TOPIC_WITH_IDEAS = /\b(outfit|decor|room|interior|garden|plant|flower|landscap|travel|destination|style|fashion|nursery|kitchen|bathroom|bedroom|living\s*room|backyard|front\s*yard|reno|renovation)\s+(idea|inspiration|inspo|look)/i;
const NO_IMAGES = /\b(plan|planning|itinerar|packing\s+list|meal\s+plan|recipe|prep|email|meeting|schedule|scheduling|weekly|prepare|budget|checklist)\b/i;
const FASHION_RE = /\b(outfit|fashion|style|cloth|clothing|dress|dresses|what to wear|wardrobe|ootd|streetwear|athleisure|capsule wardrobe|getting dressed|get dressed)\b/i;

function shouldSearchImages(userMessage: string): boolean {
  if (NO_IMAGES.test(userMessage)) return false;
  return VISUAL_INTENT.test(userMessage) || VISUAL_TOPIC_WITH_IDEAS.test(userMessage);
}

function buildImageQuery(userMessage: string): string {
  if (!FASHION_RE.test(userMessage)) return userMessage;
  const msg = userMessage.toLowerCase();
  const descriptors: string[] = [];
  if (/spring|easter|march|april|may/.test(msg)) descriptors.push("spring pastel");
  else if (/summer|june|july|august|hot|beach/.test(msg)) descriptors.push("summer lightweight");
  else if (/fall|autumn|september|october|november|thanksgiving/.test(msg)) descriptors.push("autumn warm tones");
  else if (/winter|december|january|february|christmas|holiday/.test(msg)) descriptors.push("winter cozy");
  if (/church|sunday|worship/.test(msg)) descriptors.push("elegant modest");
  else if (/date\s*night|dinner|evening/.test(msg)) descriptors.push("chic evening");
  else if (/work|office|meeting|corporate|professional/.test(msg)) descriptors.push("business casual polished");
  else if (/casual|everyday|errand|weekend/.test(msg)) descriptors.push("relaxed casual chic");
  else if (/wedding|bridal|shower/.test(msg)) descriptors.push("elegant formal");
  else if (/party|celebration|birthday/.test(msg)) descriptors.push("stylish celebration");
  if (/postpartum|new mom|nursing|breastfeed/.test(msg)) descriptors.push("comfortable flowy flattering");
  else if (/pregnant|maternity|bump/.test(msg)) descriptors.push("maternity comfortable");
  else if (/plus.?size|curv/.test(msg)) descriptors.push("flattering comfortable");
  else if (/comfort|comfy|cozy/.test(msg)) descriptors.push("comfortable soft");
  if (/dress(es)?/.test(msg)) descriptors.push("dress");
  else if (/jean|denim/.test(msg)) descriptors.push("denim outfit");
  else if (/skirt/.test(msg)) descriptors.push("skirt outfit");
  else if (/suit|blazer/.test(msg)) descriptors.push("blazer outfit");
  else descriptors.push("outfit");
  if (/boho|bohemian/.test(msg)) descriptors.push("bohemian");
  if (/minimal|minimalist|clean/.test(msg)) descriptors.push("minimalist");
  if (/trendy|trend/.test(msg)) descriptors.push("trendy modern");
  if (/modest/.test(msg)) descriptors.push("modest covered");
  if (/athleisure|sporty|athletic/.test(msg)) descriptors.push("athleisure sporty");
  if (descriptors.length === 0) descriptors.push("stylish outfit inspiration");
  return "woman wearing " + descriptors.join(" ") + " fashion photography";
}

async function searchImages(query: string): Promise<ImageResult[]> {
  try {
    const resp = await fetch(IMAGE_SEARCH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ query }),
    });
    if (!resp.ok) return [];
    const data = await resp.json();
    return data.images || [];
  } catch {
    return [];
  }
}

type StreamChatResult =
  | { status: "ok" }
  | {
      status: "expected_error";
      code: "LIMIT_REACHED" | "CHAT_GLITCH";
      message: string;
      limitReached?: boolean;
    };

async function streamChat({
  messages,
  onDelta,
  onDone,
  onToolEvent,
  userId,
  accessToken,
  googleCalConnected,
  signal,
}: {
  messages: { role: string; content: any }[];
  onDelta: (text: string) => void;
  onDone: () => void;
  // #691 — kin_tool_event arrives on its own SSE field, never inside
  // choices[0].delta.content, specifically so a client-side action (a write,
  // an Undo that deletes a row) can never be triggered by model-authored
  // text — only by this server writing this exact field.
  onToolEvent?: (event: unknown) => void;
  userId: string | null;
  accessToken: string | null;
  googleCalConnected: boolean;
  signal?: AbortSignal;
}): Promise<StreamChatResult> {
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  if (!accessToken) {
    throw new Error("Not authenticated");
  }

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ messages, currentDate, user_id: userId, google_cal_connected: googleCalConnected }),
    signal,
  });

  if (!resp.ok) {
    let err: { error?: string; limit_reached?: boolean } = {};
    try {
      err = await resp.json();
    } catch {
      err = {};
    }

    if (resp.status === 429) {
      return {
        status: "expected_error",
        code: "LIMIT_REACHED",
        message: err.error || "Daily limit reached",
        limitReached: !!err.limit_reached,
      };
    }

    if (resp.status === 400 || resp.status === 401) {
      return {
        status: "expected_error",
        code: "CHAT_GLITCH",
        message: err.error || "Glitch",
      };
    }

    return {
      status: "expected_error",
      code: "CHAT_GLITCH",
      message: err.error || "Something went wrong",
    };
  }

  if (!resp.body) throw new Error("No response body");

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
        if (parsed.kin_tool_event) onToolEvent?.(parsed.kin_tool_event);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
        if (parsed.kin_tool_event) onToolEvent?.(parsed.kin_tool_event);
      } catch {}
    }
  }

  onDone();
  return { status: "ok" };
}

// The same "added ✓" phrasing both handlers below use — names where it
// actually landed instead of always saying "to your to-dos" (a Copilot
// review on this PR caught the general-list wording running unconditionally
// even for named-list inserts).
function todoAddedMessage(item: string, listName: string | null): string {
  return `Added "${item}"${listName ? ` to your "${listName}" list` : " to your to-dos"}`;
}

// -----------------------------------------------------------------------------
// ── CONFIRM TOASTS NEVER EXPIRE ────────────────────────────────────────────
//
// A confirm toast is the ONLY record that a pending action exists. The
// kin_tool_event it came from is stream-only — kin-chat writes nothing and
// never persists the draft — so when the toast goes, the action is gone with
// it and there is no way to get it back.
//
// These shipped at `duration: 20000`. Twenty seconds is less time than it
// takes to read the message that prompted the toast, and Kin's message stays
// in the transcript afterwards describing an action that never happened. The
// worst case is complete_item/reschedule_item, which confirm at EVERY
// autonomy level by the Aug 7 decision: "mark the dentist thing done" in chat
// had a twenty-second window, and missing it looked exactly like being
// ignored.
//
// Infinity + a close button makes the decision hers rather than the clock's.
// She can act on it whenever she gets back to it, or dismiss it deliberately.
//
// ⚠️ This does NOT make the confirm durable. A refresh, a hard navigation or
// a cold start still loses it, because the pending action lives nowhere but
// this stream. That is #1654, which needs a table to store it in. This is the
// part that needed no schema change.
//
// ⚠️ Applies to CONFIRM toasts only. Receipt toasts (the ones carrying Undo)
// keep their timeout on purpose: the write already landed, the toast is
// informational, and an Undo that never goes away invites a much later
// mis-tap on a row she has moved on from.
const CONFIRM_TOAST_DURATION = Infinity;

// #691 — handlers for the todo_confirm/todo_receipt kin_tool_event create_todo
// sends on its own SSE field (see kin-chat/index.ts executeCreateTodoTool).
// Both use the same verified-write shape as the rest of the app (house rule
// 16): .select("id") back, and treat a zero-row result as a failure even
// though there's no error object.
//
// A todo_confirm event means the server did NOT write anything (level 3) —
// this toast's Confirm tap performs the actual insert, as an ordinary
// client-side write. A todo_receipt event means it already wrote (level 4+)
// — Undo just deletes that specific row.
// -----------------------------------------------------------------------------
function handleTodoConfirmTag(event: Extract<KinToolEvent, { type: "todo_confirm" }>, userId: string) {
  toast(`Kin wants to add "${event.item}"${event.listName ? ` to ${event.listName}` : ""}`, {
    duration: CONFIRM_TOAST_DURATION,
    closeButton: true,
    action: {
      label: "Confirm",
      onClick: async () => {
        try {
          const { data, error } = event.listId
            ? await supabase
                .from("todo_list_items")
                .insert({ user_id: userId, list_id: event.listId, item: event.item, checked: false })
                .select("id")
            : await supabase
                .from("notes_todos")
                .insert({ user_id: userId, item: event.item, checked: false })
                .select("id");
          if (error || !data?.length) throw error || new Error("insert returned no rows");
          toast.success(todoAddedMessage(event.item, event.listName));
        } catch (e) {
          console.error("[kin-tool] confirm create_todo failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

function handleTodoReceiptTag(event: Extract<KinToolEvent, { type: "todo_receipt" }>) {
  toast.success(todoAddedMessage(event.item, event.listName), {
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          const { data, error } = await supabase.from(event.table).delete().eq("id", event.id).select("id");
          if (error || !data?.length) throw error || new Error("delete returned no rows");
          toast("Removed");
        } catch (e) {
          console.error("[kin-tool] undo create_todo failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

// #1586 — receipt for add_grocery_items. The same shape as the to-do receipt
// above: the server already wrote (and read back), so this is a toast with
// Undo. Undo deletes only the lines this add INSERTED and re-checks the ones it
// merely un-checked, each verified with .select("id") (house rule 16). A pure
// "already on your list" receipt has nothing to undo, so it gets no button.
function handleGroceryReceiptTag(event: Extract<KinToolEvent, { type: "grocery_receipt" }>) {
  const line = groceryReceiptLine(event.added.map((a) => a.item), event.alreadyOn);
  if (event.added.length === 0) {
    toast(line);
    return;
  }
  const { deleteIds, recheckIds } = groceryUndoPlan(event.added);
  toast.success(line, {
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          if (deleteIds.length > 0) {
            const { data, error } = await supabase.from("grocery_list").delete().in("id", deleteIds).select("id");
            if (error || (data?.length ?? 0) !== deleteIds.length) throw error || new Error("delete did not remove every row");
          }
          if (recheckIds.length > 0) {
            const { data, error } = await supabase.from("grocery_list").update({ checked: true }).in("id", recheckIds).select("id");
            if (error || (data?.length ?? 0) !== recheckIds.length) throw error || new Error("re-check did not update every row");
          }
          toast("Removed from your grocery list");
        } catch (e) {
          console.error("[kin-tool] undo add_grocery_items failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

// #890 STEP 2 — receipt for save_family_fact, the write she explicitly asked
// for. Until now it was the ONLY write with no receipt and no Undo, while the
// passive [LEARN] capture she never asked for got a chip. Same wording as that
// chip, from the same naturalPhrase helper (#888), so one write does not sound
// like two different features.
//
// Undo archives BY ROW ID via undoFamilyFact, which reads its own write back
// (house rule 16). The [LEARN] chip still matches on category+key+subject and
// therefore still takes out siblings of a multi-valued key; pointing that one
// at ids too is follow-up work, not this step.
function handleFactReceiptTag(event: Extract<KinToolEvent, { type: "fact_receipt" }>) {
  const phrase = naturalPhrase({ subject: event.subject, key: event.key, value: event.value });
  toast.success(
    event.updated
      ? `Updated — I'll remember ${phrase} 💜`
      : `Got it, I'll remember ${phrase} 💜`,
    {
      action: {
        label: "Undo",
        onClick: async () => {
          const undone = await undoFamilyFact(event.id);
          if (undone) toast("Okay, forgotten.");
          else showSaveFailedToast();
        },
      },
    },
  );
}

// -----------------------------------------------------------------------------
// #692 — handlers for the task_confirm/task_receipt kin_tool_event
// create_scheduled_task sends (see kin-chat/index.ts
// executeCreateScheduledTaskTool). Same shape as the #691 todo handlers
// above: verified writes, Confirm performs the write the server didn't,
// Undo reverses one it already made.
//
// appointment_tracker is the one wrinkle: multiple appointments live as an
// array inside ONE shared scheduled_tasks row, so both the confirm insert
// and the Undo have to branch on whether this specific item shares a row
// with others (append/remove-one-item) or owns a brand-new row
// (insert/delete-the-row) — the same select-then-branch
// proSetupTaskCreation.ts's createAppointmentReminders() uses server-side,
// mirrored here client-side for the confirm tap.
// -----------------------------------------------------------------------------

function reminderNudgeAddedMessage(taskDescription: string, nextRunAt: string): string {
  const when = new Date(nextRunAt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  // #1612 — it repeats until Done, so the confirmation says so.
  return `Reminder set: "${taskDescription}" — ${when}. ${REMINDER_NUDGE_TOAST_SUFFIX}`;
}

function appointmentAddedMessage(label: string, providerName: string): string {
  return `Added "${label}" with ${providerName} to your appointment reminders`;
}

function handleTaskConfirmTag(event: Extract<KinToolEvent, { type: "task_confirm" }>, userId: string) {
  if (event.taskKind === "reminder_nudge") {
    const { row } = event;
    toast(`Kin wants to set a reminder: "${row.config.task_description}"`, {
      duration: CONFIRM_TOAST_DURATION,
    closeButton: true,
      action: {
        label: "Confirm",
        onClick: async () => {
          try {
            const { data, error } = await supabase
              .from("scheduled_tasks")
              // #1612 — stated here, not read off the event: parseReminderNudgeRow
              // copies named fields only, so a cadence on the event would be
              // dropped silently, and an older kin-chat deploy doesn't send one.
              .insert({ ...row, user_id: userId, interval_days: REMINDER_NUDGE_INTERVAL_DAYS })
              .select("id");
            if (error || !data?.length) throw error || new Error("insert returned no rows");
            toast.success(reminderNudgeAddedMessage(row.config.task_description, row.next_run_at));
          } catch (e) {
            console.error("[kin-tool] confirm create_scheduled_task (reminder_nudge) failed:", e);
            showSaveFailedToast();
          }
        },
      },
    });
    return;
  }

  const { item } = event;
  toast(`Kin wants to add an appointment reminder: "${item.name}" with ${item.provider_name}`, {
    duration: CONFIRM_TOAST_DURATION,
    closeButton: true,
    action: {
      label: "Confirm",
      onClick: async () => {
        try {
          const { data: existingTask, error: selectError } = await supabase
            .from("scheduled_tasks")
            .select("id, config")
            .eq("user_id", userId)
            .eq("task_type", "appointment_tracker")
            .maybeSingle();
          if (selectError) throw selectError;

          if (existingTask) {
            const items = [...((existingTask.config as any)?.items || []), item];
            const newConfig = { ...((existingTask.config as any) || {}), items };
            const { data, error } = await supabase
              .from("scheduled_tasks")
              .update({ config: newConfig, updated_at: new Date().toISOString() })
              .eq("id", existingTask.id)
              .select("id");
            if (error || !data?.length) throw error || new Error("update returned no rows");
          } else {
            const nowIso = new Date().toISOString();
            const { data, error } = await supabase
              .from("scheduled_tasks")
              .insert({
                user_id: userId,
                task_type: "appointment_tracker",
                display_name: "Appointments",
                schedule_day: null,
                schedule_time: "08:00",
                interval_days: 1,
                is_active: true,
                config: { items: [item] },
                next_run_at: nowIso,
              })
              .select("id");
            if (error || !data?.length) throw error || new Error("insert returned no rows");
          }
          toast.success(appointmentAddedMessage(item.name, item.provider_name));
        } catch (e) {
          console.error("[kin-tool] confirm create_scheduled_task (appointment_tracker) failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

function handleTaskReceiptTag(event: Extract<KinToolEvent, { type: "task_receipt" }>) {
  if (event.taskKind === "reminder_nudge") {
    toast.success(reminderNudgeAddedMessage(event.taskDescription, event.nextRunAt), {
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const { data, error } = await supabase.from("scheduled_tasks").delete().eq("id", event.id).select("id");
            if (error || !data?.length) throw error || new Error("delete returned no rows");
            toast("Removed");
          } catch (e) {
            console.error("[kin-tool] undo create_scheduled_task (reminder_nudge) failed:", e);
            showSaveFailedToast();
          }
        },
      },
    });
    return;
  }

  toast.success(appointmentAddedMessage(event.label, event.providerName), {
    action: {
      label: "Undo",
      onClick: async () => {
        try {
          if (event.wasNewRow) {
            const { data, error } = await supabase.from("scheduled_tasks").delete().eq("id", event.id).select("id");
            if (error || !data?.length) throw error || new Error("delete returned no rows");
          } else {
            const { data: current, error: selectError } = await supabase
              .from("scheduled_tasks")
              .select("config")
              .eq("id", event.id)
              .single();
            if (selectError) throw selectError;
            const items = ((current?.config as any)?.items || []).filter((i: any) => i.id !== event.itemId);
            const newConfig = { ...((current?.config as any) || {}), items };
            const { data, error } = await supabase
              .from("scheduled_tasks")
              .update({ config: newConfig, updated_at: new Date().toISOString() })
              .eq("id", event.id)
              .select("id");
            if (error || !data?.length) throw error || new Error("update returned no rows");
          }
          toast("Removed");
        } catch (e) {
          console.error("[kin-tool] undo create_scheduled_task (appointment_tracker) failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

// Mirrors supabase/functions/kin-chat/completionCopy.ts exactly (same two
// template strings) — Index.tsx is a frontend module, that file lives in the
// Deno function tree, so this is duplicated rather than imported across that
// boundary. "Confirmation names the specific item, never a pronoun" (#694
// DONE WHEN) is the reason these are named functions instead of an inline
// template at each toast() call.
function completeConfirmQuestion(label: string): string {
  return `Mark "${label}" done?`;
}
function rescheduleConfirmQuestion(label: string, newTimeDescription: string): string {
  return `Move "${label}" to ${newTimeDescription}?`;
}

// -----------------------------------------------------------------------------
// #694 — handlers for complete_confirm/reschedule_confirm. Unlike every
// other kin_tool_event above, these NEVER arrive pre-written — there is no
// autoExecute path for complete_item/reschedule_item at all, at any
// autonomy level (the Aug 7 "always confirm" decision). Every one of these
// toasts is the ONLY place either write happens.
//
// Undo for complete mirrors the codebase's existing convention exactly
// (TasksTab.tsx's restoreDraftItem: task_queue_items Undo is always
// {status:"draft"}, not a snapshot of whatever it was before) — the
// candidates this resolved against were already filtered to unchecked/draft,
// so there's nothing else a prior value could have been.
// -----------------------------------------------------------------------------

/**
 * ⚠️ One narrowed call per table, instead of `.from(event.table).update(...)`.
 *
 * `event.table` is a union, so `.from()` returns a union of query builders and
 * the update payload cannot resolve to a concrete row type. That was tolerated
 * while the generated types were loose; the regenerated types.ts (#848) adds
 * `RejectExcessProperties`, which is strict about update payloads, and these
 * four call sites became type errors.
 *
 * The reschedule pair was worse than a union: it built `{ [column]: value }`
 * from a computed key, which widens to `{ [x: string]: string }` — a shape
 * that matches NO table and therefore checks nothing. A typo'd column name
 * there would have compiled cleanly and silently updated zero rows.
 *
 * Branching on the table gives each call a literal table name and a literal
 * column, so both are checked against the real schema. Deliberately NOT fixed
 * with `as any` or a non-null assertion — that would have kept the compile
 * quiet and the check absent, which is the state this was already in.
 */
/**
 * #1654 — close the durable `pending_tool_confirms` row this chip answers.
 *
 * ⚠️ Load-bearing for CROSS-SURFACE safety, not for the app. The row exists so
 * her "yes" over SMS can resolve a confirm Kin raised earlier. If tapping
 * Confirm here left the row open, she could tap Confirm and then text "yes"
 * within the TTL and the write would run TWICE.
 *
 * Best-effort on purpose: the write above has already succeeded and been shown
 * to her, so a failure here must not turn a real success into an error toast.
 * The cost of failing is bounded — the row expires on its own within
 * PENDING_CONFIRM_TTL_MINUTES, and a duplicate complete is idempotent
 * (`checked = true` twice is `checked = true`).
 *
 * `pendingConfirmId` is optional: a stream produced before #1654 deployed
 * carries none, and that chip still works exactly as it did.
 */
async function resolvePendingConfirm(
  pendingConfirmId: string | undefined,
  userId: string,
  status: "confirmed" | "declined",
) {
  if (!pendingConfirmId) return;
  try {
    // `(supabase.from as any)` is this repo's pattern for a table the generated
    // types do not carry yet — see useGiftPrompts.ts, which does the same for
    // gift_prompts. types.ts is auto-generated and must never be hand-edited;
    // the migration carries `types-regen: pending` and the regen PR removes it.
    // ⚠️ `(supabase as any).from("…")` and NOT `(supabase.from as any)("…")`,
    // which is the form useGiftPrompts.ts uses. Both compile; only this one is
    // VISIBLE to src/lib/__tests__/supabaseColumnsExist.test.ts, whose scanner
    // matches a literal `.from("table")`. With the other form it attributed
    // these columns to the nearest preceding `.from()` in this file —
    // `scheduled_tasks` — and reported them as typos on that table.
    const { error } = await (supabase as any).from("pending_tool_confirms")
      .update({ status, resolved_at: new Date().toISOString() })
      .eq("id", pendingConfirmId)
      .eq("user_id", userId)
      .eq("status", "pending")
      .select("id");
    if (error) console.error("[pending_confirm] resolve failed:", error);
  } catch (e) {
    console.error("[pending_confirm] resolve threw:", e);
  }
}

type ConfirmTable = Extract<KinToolEvent, { type: "complete_confirm" }>["table"];

function updateItemDone(table: ConfirmTable, id: string, userId: string, done: boolean) {
  // ⚠️ #1654 — THREE branches, not two. This read
  //     table === "task_queue_items" ? task_queue_items : notes_todos
  // and `CompletableTable` has always had a third member: `todo_list_items`,
  // which fetchCompletionCandidates really does offer as a candidate. So
  // completing a to-do that lives in a NAMED LIST wrote `checked` — the correct
  // column — to `notes_todos`, using a `todo_list_items` id. Zero rows matched,
  // `!data?.length` threw, and she got a generic "couldn't save".
  //
  // The column being identical across the two todo tables is exactly why the
  // two-branch version looked right. Branch on the TABLE.
  if (table === "task_queue_items") {
    return supabase
      .from("task_queue_items")
      .update({ status: done ? "acted" : "draft" })
      .eq("id", id)
      .eq("user_id", userId)
      .select("id");
  }
  if (table === "todo_list_items") {
    return supabase
      .from("todo_list_items")
      .update({ checked: done })
      .eq("id", id)
      .eq("user_id", userId)
      .select("id");
  }
  return supabase
    .from("notes_todos")
    .update({ checked: done })
    .eq("id", id)
    .eq("user_id", userId)
    .select("id");
}

function updateItemWhen(
  table: ConfirmTable,
  id: string,
  userId: string,
  when: string | null,
) {
  // task_queue_items schedules with send_by; both todo tables with due_at.
  // ⚠️ #1654 — see updateItemDone: `todo_list_items` was falling into the
  // notes_todos branch and updating zero rows.
  if (table === "task_queue_items") {
    return supabase
      .from("task_queue_items")
      .update({ send_by: when })
      .eq("id", id)
      .eq("user_id", userId)
      .select("id");
  }
  if (table === "todo_list_items") {
    return supabase
      .from("todo_list_items")
      .update({ due_at: when })
      .eq("id", id)
      .eq("user_id", userId)
      .select("id");
  }
  return supabase
    .from("notes_todos")
    .update({ due_at: when })
    .eq("id", id)
    .eq("user_id", userId)
    .select("id");
}

function handleCompleteConfirmTag(event: Extract<KinToolEvent, { type: "complete_confirm" }>, userId: string) {
  toast(completeConfirmQuestion(event.label), {
    duration: CONFIRM_TOAST_DURATION,
    closeButton: true,
    action: {
      label: "Confirm",
      onClick: async () => {
        try {
          const { data, error } = await updateItemDone(event.table, event.id, userId, true);
          if (error || !data?.length) throw error || new Error("update returned no rows");
          // AFTER the write is proven, never before: a row marked confirmed over
          // a write that did not land would stop her SMS "yes" from retrying it.
          await resolvePendingConfirm(event.pendingConfirmId, userId, "confirmed");
          toast.success(`Marked "${event.label}" done`, {
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  const { data: undoData, error: undoError } = await updateItemDone(
                    event.table, event.id, userId, false,
                  );
                  if (undoError || !undoData?.length) throw undoError || new Error("undo returned no rows");
                  toast("Restored");
                } catch (e) {
                  console.error("[kin-tool] undo complete_item failed:", e);
                  showSaveFailedToast();
                }
              },
            },
          });
        } catch (e) {
          console.error("[kin-tool] confirm complete_item failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

function handleRescheduleConfirmTag(event: Extract<KinToolEvent, { type: "reschedule_confirm" }>, userId: string) {
  toast(rescheduleConfirmQuestion(event.label, event.newTimeDescription), {
    duration: CONFIRM_TOAST_DURATION,
    closeButton: true,
    action: {
      label: "Confirm",
      onClick: async () => {
        try {
          const { data, error } = await updateItemWhen(
            event.table, event.id, userId, event.newTimestamp,
          );
          if (error || !data?.length) throw error || new Error("update returned no rows");
          await resolvePendingConfirm(event.pendingConfirmId, userId, "confirmed");
          toast.success(`Moved "${event.label}" to ${event.newTimeDescription}`, {
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  const { data: undoData, error: undoError } = await updateItemWhen(
                    event.table, event.id, userId, event.priorTimestamp,
                  );
                  if (undoError || !undoData?.length) throw undoError || new Error("undo returned no rows");
                  toast("Restored");
                } catch (e) {
                  console.error("[kin-tool] undo reschedule_item failed:", e);
                  showSaveFailedToast();
                }
              },
            },
          });
        } catch (e) {
          console.error("[kin-tool] confirm reschedule_item failed:", e);
          showSaveFailedToast();
        }
      },
    },
  });
}

// Bottom nav content: 8px padding-top + ~36px tab icons + ~18px footer text + 4px padding-bottom = ~66px
// Safe-area inset is added separately via env() where used.
const NAV_BAR_HEIGHT = 72;
export const NAV_BAR_HEIGHT_WITH_SAFE_AREA = "calc(72px + env(safe-area-inset-bottom, 0px))";
// Visual breathing room between the input bar and the nav bar (or keyboard)
// so they never visually collide.
const INPUT_GAP = 12;

// ---- Chat state reducer ----
// Collapses messages + isLoading transitions into single atomic dispatches so
// WKWebView doesn't see 5 rapid renders during message send/receive on iOS.
// See Session_Notes_June22_2026_ChatFlicker_Diagnosis.md for full diagnosis.
type ChatState = {
  messages: Message[];
  isLoading: boolean;
};

type ChatAction =
  | { type: "SEND"; userMsg: Message }
  | { type: "LIMIT_BLOCK"; userMsg: Message; limitMsg: Message }
  | { type: "STREAM_UPDATE"; updater: (msgs: Message[]) => Message[] }
  | { type: "RECEIVE_DONE" }
  | { type: "ABORT_CLEANUP" }
  | { type: "ERROR"; errorMsg: Message }
  | { type: "RESET" }
  | { type: "LOAD"; messages: Message[] }
  | { type: "APPEND_MESSAGE"; msg: Message }
  // #1882 — the assistant reply's database identity. Two actions, not one
  // "set id to X or undefined": the attach targets the trailing message and
  // the clear targets a message BY id, because by the time a failed write is
  // known the message may no longer be last. Both reducers are pure functions
  // in src/lib/chatMessageIdentity.ts so they can be tested for real.
  | { type: "ATTACH_ASSISTANT_ID"; id: string }
  | { type: "CLEAR_MESSAGE_ID"; id: string };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SEND":
      // Atomic: add user msg AND flip loading in one render
      return {
        messages: [...state.messages, action.userMsg],
        isLoading: true,
      };
    case "LIMIT_BLOCK":
      return {
        ...state,
        messages: [...state.messages, action.userMsg, action.limitMsg],
      };
    case "STREAM_UPDATE":
      return {
        ...state,
        messages: action.updater(state.messages),
      };
    case "RECEIVE_DONE":
      return { ...state, isLoading: false };
    case "ABORT_CLEANUP": {
      const last = state.messages[state.messages.length - 1];
      const newMessages =
        last?.role === "assistant" && !last.content?.trim()
          ? state.messages.slice(0, -1)
          : state.messages;
      return { messages: newMessages, isLoading: false };
    }
    case "ERROR": {
      const last = state.messages[state.messages.length - 1];
      const base =
        last?.role === "assistant" && !last.content?.trim()
          ? state.messages.slice(0, -1)
          : state.messages;
      return {
        messages: [...base, action.errorMsg],
        isLoading: false,
      };
    }
    case "RESET":
      return { messages: [], isLoading: false };
    case "LOAD":
      return { messages: action.messages, isLoading: false };
    case "APPEND_MESSAGE":
      return { ...state, messages: [...state.messages, action.msg] };
    case "ATTACH_ASSISTANT_ID":
      return { ...state, messages: attachIdToTrailingAssistant(state.messages, action.id) };
    case "CLEAR_MESSAGE_ID":
      return { ...state, messages: clearMessageId(state.messages, action.id) };
    default:
      return state;
  }
}

const Index = () => {
  useStatusBar({ color: 'F0F0FF', style: 'dark' });
  const { user, session, adminPreview, proLoaded } = useAuth();
  const { connected: googleCalConnected } = useGoogleCalendar();
  const keyboardOffset = useStableKeyboardOffset();
  const keyboardOpen = keyboardOffset > 80;

  // Stores the screen height sampled while the keyboard is CLOSED — the only
  // safe time to read it. WKWebView shrinks clientHeight by the keyboard height
  // when the keyboard opens, so reading it live during open causes a double-subtract:
  // (already-shrunk clientHeight) − keyboardOffset = column ~2× too short.
  // July 3, 2026: confirmed on device — input rendered 190pt from top instead of 533pt.
  const fullHeightRef = useRef(0);

  // Ref so the raw-close listener (below) can read the current stable offset
  // without needing it in its dependency array.
  const keyboardOffsetRef = useRef(keyboardOffset);
  useEffect(() => { keyboardOffsetRef.current = keyboardOffset; }, [keyboardOffset]);

  // LOAD-BEARING: drives the flex column height from the Median keyboard listener via the
  // stable-offset state machine. Do NOT feed this from raw visualViewport — Median WKWebView
  // VV values are unreliable (see June 23-24 + July 3 session notes). Removing this restores
  // the keyboard snap bug.
  useLayoutEffect(() => {
    // Capture full height only while keyboard is confirmed closed.
    // Use MAX so minor address-bar fluctuations don't shrink the stored value.
    if (keyboardOffset <= 80) {
      fullHeightRef.current = Math.max(fullHeightRef.current, document.documentElement.clientHeight);
    }

    if (keyboardOffset > 80 && fullHeightRef.current > 0) {
      const target = fullHeightRef.current - keyboardOffset;
      // Safety clamp: if the result is less than half the full height something
      // is wrong — degrade to 100dvh (the pre-fix snap behavior) rather than
      // render a broken half-height column.
      if (target < fullHeightRef.current * 0.5) {
        console.warn("[vv-height] clamp tripped", { full: fullHeightRef.current, offset: keyboardOffset, target });
        document.documentElement.style.removeProperty("--vv-height");
      } else {
        document.documentElement.style.setProperty("--vv-height", `${target}px`);
        // Mark the open animation for the debug strip.
        (window as any).__kbDiag = (window as any).__kbDiag || {};
        (window as any).__kbDiag.animState = "anim:open";
        setTimeout(() => {
          if ((window as any).__kbDiag?.animState === "anim:open")
            (window as any).__kbDiag.animState = "idle";
        }, 300);
      }
    } else {
      document.documentElement.style.removeProperty("--vv-height");
    }

    // Expose stored full height to the KB debug strip
    (window as any).__kbDiag = (window as any).__kbDiag || {};
    (window as any).__kbDiag.fullHeight = fullHeightRef.current;
  }, [keyboardOffset]);

  // Warm the lazy tab chunks once the landing tab has painted (Aug 21).
  // Without this, the FIRST switch to any tab shows the Suspense spinner for
  // however long the chunk takes — on device, long enough to read as a flash
  // before the tab's own fade. The import() calls hit the same module paths
  // as the lazy() declarations above, so React.lazy resolves from cache and
  // the spinner never mounts. Idle-scheduled to stay off the critical path.
  useEffect(() => {
    const warm = () => {
      import("@/components/TasksTab");
      import("@/components/TodayTab");
      import("@/components/ToolsTab");
      import("@/components/MyKinTab");
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(warm, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(warm, 1500); // Safari/WKWebView fallback
    return () => window.clearTimeout(t);
  }, []);

  // Reset fullHeightRef on orientation change so the next closed-keyboard sample
  // picks up the new orientation's height instead of the stale portrait max.
  useEffect(() => {
    const onOrient = () => { fullHeightRef.current = 0; };
    window.addEventListener("orientationchange", onOrient);
    return () => window.removeEventListener("orientationchange", onOrient);
  }, []);

  // Release --vv-height on the RAW Median close signal — don't wait for the
  // 250ms CLOSE_CONFIRM debounce in useStableKeyboardOffset. The transition
  // (Change 2) absorbs any transient wobble: a brief false-close just eases
  // the column toward full height for a few frames, then the next raw
  // visible:true immediately re-arms the property.
  useEffect(() => {
    const onRaw = (e: Event) => {
      const visible = (e as CustomEvent<{ visible: boolean }>).detail.visible;
      if (!visible) {
        // Suppressed while a BottomSheet is open: iOS raw-closes between every
        // field, and resizing the shell there ratchets the page behind the
        // sheet (see openSheetRegistry). A genuine close still releases the
        // var through the DEBOUNCED writer above, so this only drops the
        // twitchy path. Chat never has a sheet open and is unaffected.
        if (!releaseVvHeightOnRawClose()) return;
        (window as any).__kbDiag = (window as any).__kbDiag || {};
        (window as any).__kbDiag.animState = "anim:close";
        setTimeout(() => {
          if ((window as any).__kbDiag?.animState === "anim:close")
            (window as any).__kbDiag.animState = "idle";
        }, 300);
      } else if (keyboardOffsetRef.current > 80 && fullHeightRef.current > 0) {
        // Transient false-close recovered: keyboard is still up — re-arm the property
        // so the column snaps back to the correct height instead of staying at 100dvh.
        const target = fullHeightRef.current - keyboardOffsetRef.current;
        if (target >= fullHeightRef.current * 0.5) {
          document.documentElement.style.setProperty("--vv-height", `${target}px`);
          (window as any).__kbDiag = (window as any).__kbDiag || {};
          (window as any).__kbDiag.animState = "idle";
        }
      }
    };
    window.addEventListener("kin:kb-raw", onRaw);
    return () => window.removeEventListener("kin:kb-raw", onRaw);
  }, []);

  // Belt-and-suspenders scroll pin: WKWebView's keyboard auto-scroll is a
  // system-level behavior that fires async and can bypass CSS overflow:hidden.
  // Reset scrollY to 0 immediately, after rAF (layout settle), and after 300ms
  // (keyboard animation settle). Resist any drift while keyboard is open.
  useEffect(() => {
    if (!keyboardOpen) return;
    window.scrollTo(0, 0);
    const raf = requestAnimationFrame(() => window.scrollTo(0, 0));
    const t = setTimeout(() => window.scrollTo(0, 0), 300);
    const onScroll = () => { if (window.scrollY > 0) window.scrollTo(0, 0); };
    window.addEventListener("scroll", onScroll, { passive: false });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [keyboardOpen]);
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    try {
      const stored = sessionStorage.getItem("kin_active_tab");
      if (
        stored === "chat" ||
        stored === "today" ||
        stored === "tools" ||
        stored === "mykin" ||
        stored === "tasks"
      ) {
        return stored;
      }
    } catch {
      // sessionStorage unavailable, fall through
    }
    return "chat";
  });
  // #673 — carries a task id from a kin:navigate-tab event through to
  // TasksTab, which scrolls it into view and briefly highlights it, then
  // clears this itself. Not persisted (sessionStorage above) — a highlight
  // is a one-shot "here's what just happened," not tab state to restore.
  const [highlightTaskId, setHighlightTaskId] = useState<string | null>(null);

  // Persist active tab within the session so a mid-session full remount
  // (HMR reload, JWT refresh redirect, etc.) restores the user's tab
  // instead of snapping them back to Chat. sessionStorage (not local) so
  // cold starts still default to Chat per the brand UX rule.
  useEffect(() => {
    try {
      sessionStorage.setItem("kin_active_tab", activeTab);
    } catch {
      // sessionStorage write failed (quota, private mode) — non-fatal
    }
  }, [activeTab]);
  const [myKinActivations, setMyKinActivations] = useState(0);
  const [todayActivations, setTodayActivations] = useState(0);

  // Chat state: messages + isLoading combined into one reducer for atomic
  // transitions (June 22 2026 flicker fix). Conversation id is a ref because
  // no UI depends on it — keeping it out of React state eliminates an entire
  // render in the message-send burst that was contributing to the flicker.
  // #1452 — the meal plan draft this thread is about, for the Save & commit
  // bar above the composer. Learned from the #1450 marker in her messages and
  // from kin-chat's meal_plan_* tool events; cleared on a new chat.
  const [mealPlanCard, setMealPlanCard] = useState<MealPlanCardState | null>(null);
  const [mealPlanCommitting, setMealPlanCommitting] = useState(false);
  const [{ messages, isLoading }, dispatch] = useReducer(chatReducer, {
    messages: [],
    isLoading: false,
  });

  // #1452 — the LAST marker in her messages names the plan this thread is
  // about. Messages emptying (new chat) clears the bar.
  useEffect(() => {
    if (messages.length === 0) {
      setMealPlanCard(null);
      return;
    }
    const runId = activeMealPlanRunFromMessages(messages);
    if (runId) setMealPlanCard((prev) => reduceMealPlanCard(prev, { type: "thread", runId }));
  }, [messages]);

  /**
   * #1452 — exactly what the card's Approve does: auto_commit_at = now(), own
   * row via RLS, read back (house rule 16). The 5-minute sweep commits the
   * plan and syncs the grocery list; the copy says so.
   */
  async function handleCommitMealPlanFromChat() {
    if (!mealPlanCard || mealPlanCard.committed || mealPlanCommitting) return;
    setMealPlanCommitting(true);
    try {
      const { data: run, error: readError } = await (supabase as any)
        .from("scheduled_task_runs")
        .select("id, review_status")
        .eq("id", mealPlanCard.runId)
        .maybeSingle();
      if (readError) throw readError;
      if (!run) throw new Error("plan not found");
      if (run.review_status !== "pending_review") {
        setMealPlanCard((prev) => reduceMealPlanCard(prev, { type: "committed", runId: mealPlanCard.runId }));
        toast(MEAL_PLAN_CARD_COPY.alreadySet);
        return;
      }
      const nowIso = new Date().toISOString();
      const { data, error } = await (supabase as any)
        .from("scheduled_task_runs")
        .update({ auto_commit_at: nowIso })
        .eq("id", mealPlanCard.runId)
        .select("id");
      if (error) throw error;
      assertWriteLanded(data, "approve meal plan");
      setMealPlanCard((prev) => reduceMealPlanCard(prev, { type: "committed", runId: mealPlanCard.runId }));
      toast.success(MEAL_PLAN_CARD_COPY.savedLine);
    } catch (err) {
      console.error("[MealPlanChat] commit failed:", err);
      toast.error(MEAL_PLAN_CARD_COPY.failed);
    } finally {
      setMealPlanCommitting(false);
    }
  }
  // Phase 3: chat history persistence. null = fresh chat; first user send
  // will create a conversations row and capture its id here.
  // Moved from useState to useRef on June 23, 2026 — see flicker notes above.
  const conversationIdRef = useRef<string | null>(null);

  // Phase 4: past-chats drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [conversations, setConversations] = useState<DrawerConversation[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerShowUpgradeFooter, setDrawerShowUpgradeFooter] = useState(false);
  // Phase 5: track whether the current conversation has just been created
  // by the in-flight send, so we can fire title-gen once after the first
  // user→assistant exchange completes. Flipped to false inside the title
  // IIFE before the network call to guarantee single-fire.
  const isFirstExchangeRef = useRef(false);
  const isAdmin = useAdmin();
  const isPro = useIsPro();
  const tasksTabEnabled = useFeature("tasksTab");
  const isProTier = isPro || (isAdmin && adminPreview);
  const [showRenoTracker, setShowRenoTracker] = useState(false);
  const [trackerBudgetId, setTrackerBudgetId] = useState<string | null>(null);
  const [showGiftTracker, setShowGiftTracker] = useState(false);
  const [showProteinTracker, setShowProteinTracker] = useState(false);
  const [showPartyPlanner, setShowPartyPlanner] = useState(false);
  const [hubPartyId, setHubPartyId] = useState<string | null>(null);
  const [showPartyPicker, setShowPartyPicker] = useState(false);
  /**
   * The party the wizard should open ON, and where to land.
   *
   * ⚠️ Before this, "Edit invite" cleared hubPartyId and opened the wizard
   * BLANK. The only route to an existing party was findResumableParty, which
   * filters status='planning' — so for a party she had already sent, the next
   * Save created a SECOND party and the guests' RSVP link pointed at the old
   * one. Passing the row through means persist() takes the updateParty branch.
   */
  const [editParty, setEditParty] = useState<import("@/lib/partyPersistence").PartyRow | null>(null);
  const [editStep, setEditStep] = useState<2 | 3>(2);
  const [showGardenPlanner, setShowGardenPlanner] = useState(false);
  // #1900 — Routines (Pro).
  const [showRoutines, setShowRoutines] = useState(false);
  // #1900 — the chat hand-off, or null for "she opened it herself".
  //
  // ⚠️ AN OBJECT WITH A NULL `draft` IS A HAND-OFF WHOSE DRAFT DID NOT PARSE,
  // and it opens the empty EDITOR. Only `null` here means the Tools tile. One
  // nullable draft could not say both, which is how the failed-parse case ended
  // up landing on the list instead.
  const [routinesHandoff, setRoutinesHandoff] = useState<RoutineHandoff | null>(null);
  const [showSummerPlanner, setShowSummerPlanner] = useState(false);
  const [showTripPlanner, setShowTripPlanner] = useState(false);
  // #1887 — which trip to open on, when she came from a trip block on Today.
  // null means "open the trip list", which is every existing caller.
  const [initialTripId, setInitialTripId] = useState<string | null>(null);
  const openTripPlanner = (tripId?: string) => {
    setInitialTripId(tripId ?? null);
    setShowTripPlanner(true);
  };
  const { limitReached, markLimitReached, clearLimitFlag } = useMessageLimit();
  // Admins AND Pro are exempt from the daily cap both server-side (kin-chat's
  // messageCapLogic skips the count check when isPro || isAdmin) and client-side
  // (we never show the limit UI or block sends, even if a stale localStorage
  // flag is set from a prior day or from before an upgrade).
  //
  // ⚠️ #379: Pro used to be missing here, so a free user who hit the 15-message
  // cap and then bought Pro the same day stayed blocked from Pro's headline
  // benefit until local midnight — the block was entirely client-side, the
  // server would have served her. Do not narrow this back to admin-only.
  //
  // The !proLoaded arm FAILS OPEN on purpose. While the entitlement row is
  // unresolved we do not apply the client flag at all: the server 429 is the
  // real enforcement, so a sub-second open window costs nothing, while failing
  // closed flashes a disabled composer at a mom who just paid to remove it.
  const effectiveLimitReached = isAdmin || isPro || !proLoaded ? false : limitReached;




  const LIMIT_MESSAGE = "Hey mama, you've hit your 15 free messages for today. I'll be right here when you come back tomorrow. 💜";
  const chatInputRef = useRef<{ focus: () => void; setText: (text: string) => void }>(null);
  const [pendingChatMessage, setPendingChatMessage] = useState<string | null>(null);
  const chatMessagesRef = useRef<ChatMessagesHandle>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { momTypes, onboardingComplete, loading: momTypesLoading, refetch: refetchMomTypes } = useMomTypes();
  const [showMomSelector, setShowMomSelector] = useState(false);
  const [showKidsCrew, setShowKidsCrew] = useState(false);
  const [kidsCrewDismissed, setKidsCrewDismissed] = useState(false);
  // Set immediately (alongside the localStorage write) by the X-out dismiss
  // handler below, so the modal closes without waiting on an effect. The
  // gate effect below does NOT rely on this alone for the persisted read —
  // it checks localStorage directly, synchronously, in the same pass it
  // decides whether to open the selector. An earlier version hydrated this
  // from localStorage via a separate useEffect keyed on [user]; that
  // hydration landed one render behind the gate effect's own first run, so
  // on every fresh mount the gate effect read a stale `false` here and
  // wrongly reopened the selector, which then never closed itself back up
  // (#190 regression, caught same-day via live Median testing).
  const [momTypeDismissed, setMomTypeDismissed] = useState(false);
  // One-time name capture: fires after the tutorial for brand-new signups
  // (before Mom Type Selector) and as a one-time nudge for existing accounts
  // with no name on file — same component, same gate, see the effect below.
  // nameCaptureDismissed mirrors momTypeDismissed's role: an immediate
  // in-memory flag set alongside the localStorage write, NOT the source of
  // truth for the persisted read (the gate effect below checks localStorage
  // directly, synchronously — the #190 lesson from this morning).
  const [showNameCapture, setShowNameCapture] = useState(false);
  const [nameCaptureDismissed, setNameCaptureDismissed] = useState(false);
  const { prefs, loading: prefsLoading, refetchPreferences } = usePreferences();
  // Derived from context — null while prefs are loading (preserves the three-state semantics).
  const hasKnownKids = prefsLoading
    ? null
    : prefs.known_kid_names.some((n: any) => typeof n === "string" && n.trim().length > 0);

  // Tutorial shows FIRST for any signed-in user who hasn't completed onboarding yet
  const showWelcomeOnboarding =
    !momTypesLoading && !!user && onboardingComplete === false;

  // Onboarding sequence gate. Each step waits for the previous one to be done.
  // - Tour: handled via showWelcomeOnboarding above (onboarding_complete === false)
  // - Name capture: no name on file (first_name, or an SSO full_name/name) yet
  // - Mom types: onboarding_complete === true AND momTypes is empty
  // - Kid names: momTypes has entries AND no known kids on file
  // Picks up correctly on refresh mid-flow.
  useEffect(() => {
    if (!user) return;
    if (momTypesLoading) return;
    if (showWelcomeOnboarding) return; // tour first

    // Name capture — fires once, before Mom Type, for anyone with no name.
    // getFirstName() already treats an SSO-supplied full_name/name as "has a
    // name" (same coalesce order the free-tier kin-chat FAMILY block uses),
    // so providers that DID hand over a name skip this popup entirely.
    // Read both the in-memory flag AND localStorage synchronously, right
    // here — no separate hydration effect (that pattern is exactly what
    // caused the Mom Type X-out race fixed this morning).
    const hasName = !!getFirstName(user);
    const nameCaptureShownPersisted =
      nameCaptureDismissed || localStorage.getItem(`kin_name_capture_shown_${user.id}`) === 'true';
    if (!hasName && !nameCaptureShownPersisted) {
      if (!showNameCapture) setShowNameCapture(true);
      return;
    }

    const momTypesEmpty = !momTypes || momTypes.length === 0;
    // Read the persisted flag synchronously, right here, same as the Kids
    // Crew check below it -- no separate hydration effect, no stale render.
    const momTypeDismissedPersisted =
      momTypeDismissed || localStorage.getItem(`kin_mom_type_dismissed_${user.id}`) === 'true';
    if (momTypesEmpty && !momTypeDismissedPersisted) {
      if (!showMomSelector) setShowMomSelector(true);
      return;
    }
    // Mom types done — check kids gate
    // Scoped per-user (#190) — this key used to be a single global flag, so
    // once ANY account on a device skipped Kids Crew, every subsequent
    // account on that same device silently never saw the step again.
    const kidsCrewSkippedPersisted = localStorage.getItem(`kin_kids_crew_skipped_${user.id}`) === 'true';
    if (hasKnownKids === false && !showKidsCrew && !showMomSelector && !kidsCrewDismissed && !kidsCrewSkippedPersisted) {
      setShowKidsCrew(true);
    }
  }, [user, momTypesLoading, showWelcomeOnboarding, momTypes, hasKnownKids, showMomSelector, showKidsCrew, kidsCrewDismissed, momTypeDismissed, showNameCapture, nameCaptureDismissed]);

  /**
   * #1896 — "Snap a sign-up sheet".
   *
   * ⚠️ This never becomes a model turn. The photo goes straight to
   * signup-sheet-parse, which reads her names server-side, files what she
   * picked, and WRITES KIN'S REPLY ITSELF from what actually landed. Letting
   * the model narrate this would be the house-rule-16 failure in prose: it
   * would describe writes it did not see the result of.
   */
  /**
   * The first-pass read of the sheet, kept between "Which row is yours?" and
   * her answer. Filing sends THIS back rather than the photo: a second vision
   * call numbers the rows differently, so her chosen index would point at a
   * different row (Oct 27 2026).
   */
  const signupSheetRef = useRef<unknown>(null);

  /**
   * True while the sign-up flow is unresolved: the photo was unreadable,
   * nothing landed, or the "which row" picker is open. Nov 2 2026: after a
   * failed read she sent the photo again through the chat camera, and plain
   * chat answered "I think there's been a mix-up" with no idea she was
   * mid-flow. While this is set, a photo goes back to the sheet reader.
   */
  const signupRetryRef = useRef(false);

  /**
   * Copilot on #1988, all three acted on:
   *  - GENERATION. Bumped whenever she leaves the flow (new conversation,
   *    reset, a text-only send) and whenever a new read starts. A response
   *    that comes back under an older generation is dropped, so it can never
   *    re-arm retry routing or land a picker in a conversation she has left.
   *  - SINGLE FLIGHT. One sheet request at a time. A second photo while one
   *    is reading would race it for the same placeholder and sheet.
   *  - PER-REQUEST PLACEHOLDER. Each request's bubble carries its own id and
   *    is settled by that id, never by "the first pending bubble".
   */
  const signupGenRef = useRef(0);
  const signupInFlightRef = useRef(false);
  const signupReqSeqRef = useRef(0);

  /** Leave the sign-up flow: no retry routing, and any in-flight answer is dropped. */
  const endSignupFlow = useCallback(() => {
    signupGenRef.current += 1;
    signupRetryRef.current = false;
    signupInFlightRef.current = false;
    signupSheetRef.current = null;
  }, []);

  /** Replace request `reqId`'s placeholder with `next`; drop it if the placeholder is gone. */
  const replaceSignupPlaceholder = useCallback((reqId: number, next: Message | null) => {
    dispatch({
      type: "STREAM_UPDATE",
      updater: (msgs) => {
        const at = msgs.findIndex((m) => m.signupReqId === reqId);
        if (at < 0) return next ? [...msgs, next] : msgs;
        if (!next) return msgs.filter((_, i) => i !== at);
        return msgs.map((m, i) => (i === at ? next : m));
      },
    });
  }, []);

  /**
   * Settle request `reqId` with Kin's words. `retry`: the flow ended WITHOUT
   * filing, so a photo she sends next goes back to the sheet reader.
   */
  const settleSignupPending = useCallback((reqId: number, content: string, retry = true, proUpsell = false) => {
    signupRetryRef.current = retry;
    replaceSignupPlaceholder(reqId, {
      role: "assistant",
      content,
      ...(retry ? { signupRetry: true } : {}),
      // #1998 — the function's own flag, never inferred from the text.
      ...(proUpsell ? { signupProUpsell: true } : {}),
    });
  }, [replaceSignupPlaceholder]);

  const callSignupSheet = useCallback(
    async (
      reqId: number,
      req: { images: { base64: string; mediaType: string }[] } | { sheet: unknown; chosenRows: number[] },
    ) => {
      const gen = signupGenRef.current;
      const stale = () => gen !== signupGenRef.current;
      if (!session?.access_token) {
        signupInFlightRef.current = false;
        settleSignupPending(reqId, "You're signed out. Sign back in and snap it again.");
        return;
      }
      const filing = "sheet" in req;
      signupInFlightRef.current = true;
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/signup-sheet-parse`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              ...(filing
                ? { sheet: req.sheet, chosen_rows: req.chosenRows }
                : { images: req.images }),
              today_iso: new Date().toISOString().slice(0, 10),
            }),
          },
        );
        const data = await res.json().catch(() => null);
        if (stale()) { replaceSignupPlaceholder(reqId, null); return; }
        signupInFlightRef.current = false;

        if (!res.ok || !data) {
          settleSignupPending(reqId, data?.error ?? SIGNUP_UNREADABLE);
          return;
        }

        if (data.unreadable) {
          settleSignupPending(reqId, data.reply ?? SIGNUP_UNREADABLE);
          return;
        }

        if (data.needs_choice && !filing) {
          signupSheetRef.current = data.sheet ?? null;
          signupRetryRef.current = true;
          const matched: number[] = data.matched_rows ?? [];
          replaceSignupPlaceholder(reqId, {
            role: "assistant",
            content: matched.length > 0 ? SIGNUP_CONFIRM_PROMPT : SIGNUP_CHOICE_PROMPT,
            signupPicker: {
              sheetTitle: data.sheet_title ?? "",
              rows: data.rows ?? [],
              matchedRows: matched,
              sheet: data.sheet ?? null,
            },
          });
          return;
        }

        signupSheetRef.current = null;
        trackFeature("signup_sheet_filed", {
          rows: (data.filed?.events ?? 0) + (data.filed?.cards ?? 0),
        });
        const landed = Number(data.filed?.events ?? 0) + Number(data.filed?.cards ?? 0)
          + (Array.isArray(data.filed?.already) ? data.filed.already.length : 0);
        settleSignupPending(reqId, data.reply ?? "Filed. 📋", landed === 0, data.pro_upsell === true);
      } catch (e) {
        console.error("[Index] signup sheet call failed:", e);
        if (stale()) { replaceSignupPlaceholder(reqId, null); return; }
        signupInFlightRef.current = false;
        settleSignupPending(reqId, "That didn't go through. Want to try the photo again?");
      }
    },
    [session?.access_token, settleSignupPending, replaceSignupPlaceholder],
  );

  const handleSignupSheet = useCallback(
    (images: { base64: string; mediaType: string }[]) => {
      if (signupInFlightRef.current) {
        toast("Still reading the last one. Give Kin a sec 💜");
        return;
      }
      // A new read starts a new generation, and every older picker still on
      // screen is retired: its sheet is not the one now in signupSheetRef,
      // and a tap on it must not file against a different read.
      signupGenRef.current += 1;
      signupSheetRef.current = null;
      const reqId = ++signupReqSeqRef.current;
      dispatch({
        type: "STREAM_UPDATE",
        updater: (msgs) => [
          ...msgs.map((m) =>
            m.signupPicker && !m.signupPicker.answered
              ? { ...m, signupPicker: { ...m.signupPicker, answered: true } }
              : m,
          ),
          { role: "assistant", content: "Reading your sheet…", signupPending: "reading", signupReqId: reqId },
        ],
      });
      void callSignupSheet(reqId, { images });
    },
    [callSignupSheet],
  );

  const handleSignupRowsChosen = useCallback(
    (messageIndex: number, rowIndexes: number[]) => {
      // The CLICKED picker's own read, always. Never the shared ref: a newer
      // read would number its rows differently (Copilot on #1988).
      const sheet = messages[messageIndex]?.signupPicker?.sheet ?? null;
      if (rowIndexes.length > 0 && signupInFlightRef.current) {
        toast("Still working on the last one. Give Kin a sec 💜");
        return;
      }
      const reqId = ++signupReqSeqRef.current;
      dispatch({
        type: "STREAM_UPDATE",
        updater: (msgs) => {
          const marked = msgs.map((m, i) =>
            i === messageIndex && m.signupPicker
              ? { ...m, signupPicker: { ...m.signupPicker, answered: true } }
              : m,
          );
          // "None of these" files nothing and needs no request.
          if (rowIndexes.length === 0) {
            return [...marked, { role: "assistant", content: "No problem. Nothing filed." }];
          }
          return [...marked, { role: "assistant", content: SIGNUP_FILING, signupPending: "filing", signupReqId: reqId }];
        },
      });
      if (rowIndexes.length === 0) { endSignupFlow(); return; }
      if (!sheet) {
        settleSignupPending(reqId, "I've lost that sheet. Snap it again and I'll file it.");
        return;
      }
      void callSignupSheet(reqId, { sheet, chosenRows: rowIndexes });
    },
    [callSignupSheet, messages, settleSignupPending, endSignupFlow],
  );

  useEffect(() => {
    if (user?.id) startSessionForUser(user.id);
  }, [user?.id]);

  // One-time "first forwarded email" toast — a single check on app open, NOT
  // polling and NOT a visibility/focus listener (deliberately simpler than
  // the Pro-purchase polling pattern). kin-inbox sets
  // inbox_addresses.inbox_setup_completed_at the moment a user's first email
  // is genuinely processed (any parser); this surfaces that moment for users
  // who don't have push enabled (or just missed the push). Client-side "seen"
  // flag is localStorage, same pattern as kin_pro_memory_setup_done.
  //
  // ⚠️ #1892 — AND only when that timestamp is RECENT. The localStorage flag
  // is per-device, so a new phone, the browser instead of the Median app, a
  // reinstall or iOS clearing web data all showed this again, however long ago
  // her first email actually was. Kasandra got it on Oct 22 for an email she
  // forwarded on Aug 4. shouldShowFirstEmailToast is the 72-hour window.
  useEffect(() => {
    if (!user?.id) return;
    const seenKey = `kin_inbox_first_email_seen_${user.id}`;
    if (localStorage.getItem(seenKey) === "true") return;
    void (async () => {
      try {
        const { data, error } = await supabase
          .from("inbox_addresses")
          .select("inbox_setup_completed_at")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) {
          console.error("[Index] first-email toast check failed:", error);
          return;
        }
        const completedAt = (data as any)?.inbox_setup_completed_at ?? null;
        if (shouldShowFirstEmailToast(completedAt, new Date())) {
          // Copy, no em dash (#1892).
          toast("Your first forwarded email landed. Check out your itinerary 💜");
          localStorage.setItem(seenKey, "true");
        } else if (completedAt) {
          // ⚠️ STILL set the flag. Her first email is long past, so the toast
          // is wrong, but the read must stop running on this device every time
          // she opens the app. Without this the window is re-evaluated forever
          // on an answer that can never change.
          localStorage.setItem(seenKey, "true");
        }
      } catch (e) {
        console.error("[Index] first-email toast check threw:", e);
      }
    })();
  }, [user?.id]);

  // Honor `?tab=today|mykin|tools|chat` (used by tapped push notifications
  // to deep-link into the relevant tab on cold start). Runs once on mount;
  // strips the param so refreshes don't keep re-applying it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t === "today" || t === "mykin" || t === "tools" || t === "chat" || t === "tasks") {
      setActiveTab(t as TabId);
      // The ?tab= param is only set by push-notification deep links — fire an
      // analytics event so we can distinguish push-launched opens from
      // organic tab taps. Fire-and-forget.
      trackFeature("app_open_from_push", { tab: t });
      params.delete("tab");
      const next = params.toString();
      const url = window.location.pathname + (next ? `?${next}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top on chat tab activation — must be instant to avoid flash
  useEffect(() => {
    if (activeTab === "chat") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    }
    if (activeTab === "mykin") {
      setMyKinActivations((n) => n + 1);
    }
    if (activeTab === "today") {
      setTodayActivations((n) => n + 1);
    }
  }, [activeTab]);

  // Exit voice mode automatically when the user navigates away from the chat tab
  const deactivateVoiceModeRef = useRef<() => void>(() => {});
  useEffect(() => {
    deactivateVoiceModeRef.current = realtimeVoice.end;
  });
  useEffect(() => {
    if (activeTab !== "chat") {
      deactivateVoiceModeRef.current();
    }
  }, [activeTab]);

  // Defense in depth: if the Tasks flag is ever off (tasksTab is GA and
  // unconditioned today, so useFeature resolves it to true instantly and
  // this can't currently fire — kept in case the flag regains a gate),
  // bounce off the Tasks panel to Chat.
  useEffect(() => {
    if (activeTab === "tasks" && !tasksTabEnabled) {
      setActiveTab("chat");
    }
  }, [activeTab, tasksTabEnabled]);

  // When a cross-tab pre-fill is queued (e.g. "Ask Kin for recipe" from Tasks),
  // wait until chat tab is active and the input ref is ready, then populate it.
  useEffect(() => {
    if (!pendingChatMessage || activeTab !== "chat") return;
    const apply = () => {
      if (chatInputRef.current) {
        chatInputRef.current.setText(pendingChatMessage);
        setPendingChatMessage(null);
      }
    };
    // Small delay so ChatInput has mounted after the tab switch animation
    const t = setTimeout(apply, 100);
    return () => clearTimeout(t);
  }, [pendingChatMessage, activeTab]);

  // Listen for cross-component tab navigation (e.g. from SavedToast "My Kin" link)
  useEffect(() => {
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<{ tab?: TabId; highlightTaskId?: string }>).detail;
      if (detail?.tab) setActiveTab(detail.tab);
      // #673 — always set from the event, including to null, so a later plain
      // nav (no highlightTaskId) can't be haunted by a highlight from a
      // previous one. TasksTab clears this itself once it's used it.
      setHighlightTaskId(detail?.highlightTaskId ?? null);
    };
    window.addEventListener("kin:navigate-tab", onNav as EventListener);
    return () => window.removeEventListener("kin:navigate-tab", onNav as EventListener);
  }, []);

  useEffect(() => {
    const setHiddenInputTabIndex = (root: ParentNode) => {
      root.querySelectorAll<HTMLInputElement>('input[type="hidden"]').forEach((input) => {
        input.tabIndex = -1;
      });
    };
    setHiddenInputTabIndex(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLInputElement && node.type === "hidden") {
            node.tabIndex = -1;
          }
          if (node instanceof Element) {
            setHiddenInputTabIndex(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const sendMessage = useCallback(async (
    input: string,
    imageDataOrDisplay?: { base64: string; mediaType: string; displayText?: string } | string,
    images?: { base64: string; mediaType: string }[],
  ) => {
    // When the user sends, always re-pin to bottom even if she had scrolled up.
    chatMessagesRef.current?.resetScrollLock();
    let imageData: { base64: string; mediaType: string } | undefined;
    let displayOverride: string | undefined;
    if (typeof imageDataOrDisplay === "string") {
      displayOverride = imageDataOrDisplay;
    } else if (imageDataOrDisplay) {
      imageData = { base64: imageDataOrDisplay.base64, mediaType: imageDataOrDisplay.mediaType };
      if (imageDataOrDisplay.displayText) displayOverride = imageDataOrDisplay.displayText;
    }

    // Opt-in multi-image list (from ChatInput). Falls back to the single-image
    // path when the array is absent, so every other caller keeps working.
    const imageList = images && images.length > 0
      ? images
      : imageData
        ? [imageData]
        : [];

    const displayText = displayOverride || input || "📷 Photo";
    const userMsg: Message = {
      role: "user",
      content: displayText,
      userImage: imageList.length > 0 ? `data:${imageList[0].mediaType};base64,${imageList[0].base64}` : undefined,
      userImages: imageList.length > 0 ? imageList.map((img) => `data:${img.mediaType};base64,${img.base64}`) : undefined,
    };

    // Mid sign-up flow, a photo is another try at the sheet, not a new chat
    // turn (Nov 2 2026). Text with no photo ends the flow: she has moved on.
    if (signupRetryRef.current && !displayOverride) {
      if (imageList.length > 0) {
        if (signupInFlightRef.current) {
          toast("Still reading the last one. Give Kin a sec 💜");
          return;
        }
        dispatch({ type: "APPEND_MESSAGE", msg: userMsg });
        handleSignupSheet([imageList[0]]);
        return;
      }
      endSignupFlow();
    }

    if (effectiveLimitReached) {
      const limitMsg: Message = {
        role: "assistant",
        content: LIMIT_MESSAGE,
      };
      dispatch({ type: "LIMIT_BLOCK", userMsg, limitMsg });
      return;
    }

    let apiContent: any;
    if (imageList.length > 0) {
      const textForApi = input || "What can I make with this? Give me 3 quick meal or snack ideas using mostly what you see, and let me know if I'd need to grab anything small to make it work.";
      apiContent = [
        // Images first, then the text block — same order as the single-image path.
        ...imageList.map((img) => ({
          type: "image",
          source: {
            type: "base64",
            media_type: img.mediaType,
            data: img.base64,
          },
        })),
        { type: "text", text: textForApi },
      ];
    } else {
      apiContent = input;
    }

    const updatedMessages = [
      ...messages.map(({ role, content }) => ({ role, content })),
      { role: "user" as const, content: apiContent },
    ];
    // Atomic state transition: adds user msg AND flips isLoading=true in
    // a single render. Replaces the prior two separate setState calls that
    // were producing two separate renders (and visible flicker on iOS).
    dispatch({ type: "SEND", userMsg });

    // ---- Phase 3: chat history persistence (fire-and-forget) ----
    // 1) Ensure a conversation row exists for this chat.
    // 2) Save the user message.
    // Both wrapped to never block or surface errors to the chat flow.
    let convId: string | null = conversationIdRef.current;
    const persistUserId = user?.id ?? null;
    console.log("[chat-persist] reached persistence checkpoint", {
      persistUserId,
      userId: user?.id,
      authUserExists: !!user,
    });
    if (persistUserId) {
      console.log("[chat-persist] entered persist block", {
        persistUserId,
        existingConvId: conversationIdRef.current,
        hasUser: !!user,
      });
      try {
        if (!convId) {
          // #1530 — a transport blip here used to lose the whole exchange, and
          // on a first session that exchange is her introduction to Kin.
          // Retries transport failures only; see @/lib/createConversationWithRetry
          // for why status 0 is the signal and why retrying a POST is an
          // accepted trade rather than a free win.
          const created = await createConversationWithRetry(() =>
            supabase
              .from("conversations")
              .insert({ user_id: persistUserId })
              .select("id")
              .single(),
          );
          if (created.id) {
            convId = created.id;
            // Ref write — no re-render fires here. This was previously a
            // setState that contributed to the 5-render burst on iOS.
            conversationIdRef.current = convId;
            // Phase 5: mark this as a fresh conversation so the post-
            // assistant IIFE will trigger one-shot title generation.
            isFirstExchangeRef.current = true;
            console.log("[chat-persist] created conversation", convId);
          } else {
            // reportError, not console.error + captureException: it sets the
            // scope BEFORE logging, so the event captureConsoleIntegration
            // synthesises carries these tags too. The bare pair produces two
            // events and tags only the second (#1255).
            reportError("[chat-persist] Failed to create conversation", created.lastError, {
              tags: {
                area: "chat-persist",
                writer: "create-conversation",
                error_class:
                  created.lastStatus === TRANSPORT_STATUS ? "network" : "database",
              },
              extra: { attempts: created.attempts, status: created.lastStatus },
            });
          }
        }
      } catch (err) {
        reportError("[chat-persist] Failed to create conversation", err, {
          tags: { area: "chat-persist", writer: "create-conversation", error_class: "thrown" },
        });
      }

      if (convId) {
        const convIdForUserMsg = convId;
        void (async () => {
          // Sentry JAVASCRIPT-REACT-2Q — one dropped connection here deleted
          // HER message from history while the screen showed it saved, leaving
          // a reload with Kin's reply and no question in front of it.
          //
          // Still fire-and-forget: this whole IIFE is detached, nothing awaits
          // it, and the retry budget below runs entirely inside it. The send
          // and Kin's streaming reply are not delayed by a millisecond, and a
          // failure stays silent — there is nothing she could do about a
          // dropped packet, so a toast would only make her distrust a message
          // that is already on her screen.
          //
          // Retries transport failures ONLY; see @/lib/saveMessageWithRetry for
          // why status 0 is the signal and why a real DB/RLS refusal is
          // reported instead of retried.
          //
          // ⚠️ `messageId` is minted ONCE, outside the thunk, and the write is
          // an UPSERT keyed on it — so a retry is idempotent instead of a
          // second row.
          //
          // `status === 0` means fetch rejected, which is usually "it never
          // arrived" — but a connection dropped AFTER the server committed
          // lands there too, and a plain insert would then write her message
          // twice. #1530 accepted that trade because its duplicate was one
          // empty conversation row. Here it is a visible line in her
          // transcript, and **nothing in the app can delete a single message**
          // — `from("messages")` has no DELETE anywhere in src/, only the two
          // inserts and the reads — so the duplicate would be permanent and
          // hers to live with. On conflict this updates the same row with
          // byte-identical content instead, which is a no-op.
          //
          // ⚠️ #1882 — the ASSISTANT message now mints an id here too, and
          // deliberately did NOT become an upsert: it has no retry budget, so
          // there is no second write for `ON CONFLICT` to collapse. The id
          // there exists so the message can be ADDRESSED, not so a retry is
          // idempotent. Keep the two reasons distinct; copying this pattern
          // onto a third writer without its own retry would be cargo cult.
          //
          // A minted id colliding with an EXISTING row is not a merge risk: the
          // UPDATE policy is `auth.uid() = user_id`, so a collision with
          // another user's row matches zero rows and is reported as a failure
          // rather than silently overwriting anything.
          //
          // ⚠️ `sentAt` is captured HERE, before the first attempt, and the
          // thunk closes over it so every retry sends the SAME value. It must
          // stay outside the thunk: moved inside, it would be re-evaluated per
          // attempt and reintroduce exactly the bug it exists to stop.
          //
          // Without it each attempt takes the database's `created_at = now()`,
          // and history loads in ascending `created_at` order (:2128). The
          // assistant row sets no `created_at`, so it is stamped whenever
          // streaming finishes — which after a 1s + 3s backoff can be EARLIER
          // than the retried user row. Her question would then reload
          // underneath the answer it asked for, and in the duplicate case one
          // copy either side of it. Pinning the moment she actually hit send is
          // also the more honest timestamp than "whenever the insert landed".
          const sentAt = new Date().toISOString();
          const messageId = crypto.randomUUID();
          const saved = await saveMessageWithRetry(() =>
            supabase
              .from("messages")
              .upsert({
                id: messageId,
                conversation_id: convIdForUserMsg,
                user_id: persistUserId,
                role: "user",
                content: displayText,
                created_at: sentAt,
              })
              // House rule 16 — an RLS refusal returns 200 with no error and no
              // rows, so the bare insert could not tell a save from a silent
              // refusal. Asking for the row back is what makes the check real.
              .select("id"),
          );

          if (saved.saved) {
            console.log("[chat-persist] saved user message", convIdForUserMsg);
          } else {
            // reportError, not console.error + the error as a 2nd arg: the bare
            // pair produces two Sentry events, titles the one that reaches
            // #bugs as "[object Object]", and tags neither (#1255). Fired only
            // here — after every retry is spent — so a blip that healed on
            // attempt 2 does not page anyone.
            reportError("[chat-persist] Failed to save user message", saved.lastError, {
              tags: {
                area: "chat-persist",
                writer: "user-message",
                error_class: saved.errorClass,
              },
              extra: { attempts: saved.attempts, status: saved.lastStatus },
            });
          }
        })();
      }
    }

    const wantImages = imageList.length === 0 && shouldSearchImages(input);
    let imagePromise: Promise<ImageResult[]> | null = null;
    if (wantImages) {
      imagePromise = searchImages(buildImageQuery(input));
    }

    let assistantSoFar = "";
    // Receipts for facts learned on THIS turn, in arrival order. A ref rather
    // than state: upsertAssistant reads it synchronously while streaming, and a
    // state read there would see the previous render's value.
    const learnReceiptsRef = { current: [] as LearnReceipt[] };
    // #1434 — these four are also the source for the PERSIST strip below, via
    // stripModalTags. They were four independent locals and the persist used
    // only two of them, which is how [OPEN_GIFT_TRACKER] and
    // [OPEN_SUMMER_PLANNER] reached messages.content verbatim.
    const [TRACKER_TAG, GIFT_TAG, GARDEN_TAG, SUMMER_TAG, PROTEIN_TAG, ROUTINES_TAG] = MODAL_TAGS;
    const upsertAssistant = (chunk: string, images?: ImageResult[]) => {
      assistantSoFar += chunk;
      let displayText = assistantSoFar;
      if (displayText.includes(TRACKER_TAG)) {
        displayText = displayText.replace(TRACKER_TAG, "").trimEnd();
        setTimeout(() => setShowRenoTracker(true), 500);
      }
      if (displayText.includes(GIFT_TAG)) {
        displayText = displayText.replace(GIFT_TAG, "").trimEnd();
        setTimeout(() => setShowGiftTracker(true), 500);
      }
      if (displayText.includes(GARDEN_TAG)) {
        displayText = displayText.replace(GARDEN_TAG, "").trimEnd();
        setTimeout(() => setShowGardenPlanner(true), 500);
      }
      if (displayText.includes(SUMMER_TAG)) {
        displayText = displayText.replace(SUMMER_TAG, "").trimEnd();
        setTimeout(() => setShowSummerPlanner(true), 500);
      }
      if (displayText.includes(PROTEIN_TAG)) {
        displayText = displayText.replace(PROTEIN_TAG, "").trimEnd();
        setTimeout(() => setShowProteinTracker(true), 500);
      }
      // #1900 — ROUTINES IS THE ONE THAT DOES NOT OPEN FROM HERE, and the
      // difference is the payload.
      //
      // ⚠️ Every tag above fires its modal mid-stream, which is right when the
      // marker is the whole message. Routines also carries a hidden
      // [ROUTINE_DRAFT] block, and the two can arrive in either order — so
      // opening on first sight of the marker would open the editor from a draft
      // that is half-streamed or not yet there at all, and "prefilled with
      // nothing" is indistinguishable to her from "Kin lost what I said".
      //
      // So the marker and the payload are stripped HERE, every chunk, and the
      // modal opens from the COMPLETE reply after the stream ends (below).
      if (displayText.includes(ROUTINES_TAG)) {
        displayText = displayText.replace(ROUTINES_TAG, "").trimEnd();
      }
      // ⚠️ AND THE MARKER STILL ARRIVING. A chunk boundary can fall inside it,
      // and `[OPEN_ROUTIN` matches nothing above, so it rendered for a frame.
      // ⚠️ Routines' two markers only; the five older [OPEN_*] tags have the
      // same gap and are deliberately untouched, because widening to them
      // changes four other features' streaming behaviour.
      displayText = stripTrailingPartialMarker(displayText, ROUTINES_TAG);
      // ⚠️ RUNS UNCONDITIONALLY, not only alongside the marker. A draft block
      // with no marker is still raw JSON streaming past her, and mid-stream
      // this truncates at an unclosed opener so the payload never shows at all.
      // It heals on the chunk the closer arrives in, because displayText is
      // rebuilt from assistantSoFar every time rather than edited in place.
      displayText = stripRoutineDraft(displayText).trimEnd();
      dispatch({
        type: "STREAM_UPDATE",
        updater: (prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant") {
            return prev.map((m, i) =>
              i === prev.length - 1
                ? {
                    ...m,
                    content: displayText,
                    ...(images ? { images } : {}),
                    // #915 — the chip's source of truth. Empty until the server
                    // confirms a write, so a chip can never appear for a fact
                    // that was not saved.
                    ...(learnReceiptsRef.current.length
                      ? { learnReceipts: learnReceiptsRef.current }
                      : {}),
                  }
                : m
            );
          }
          return [...prev, {
            role: "assistant",
            content: displayText,
            ...(images ? { images } : {}),
            ...(learnReceiptsRef.current.length
              ? { learnReceipts: learnReceiptsRef.current }
              : {}),
          }];
        },
      });
    };

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (!session?.access_token) {
        dispatch({ type: "RECEIVE_DONE" });
        window.location.href = "/signin";
        return;
      }
      const result = await streamChat({
        messages: updatedMessages,
        userId: user?.id ?? null,
        accessToken: session.access_token,
        googleCalConnected,
        signal: controller.signal,
        onDelta: (chunk) => upsertAssistant(chunk),
        onToolEvent: (raw) => {
          const uid = user?.id;
          if (!uid) return;
          const event = parseKinToolEvent(raw);
          if (event?.type === "todo_confirm") handleTodoConfirmTag(event, uid);
          else if (event?.type === "todo_receipt") handleTodoReceiptTag(event);
          else if (event?.type === "grocery_receipt") handleGroceryReceiptTag(event);
          else if (event?.type === "fact_receipt") {
            // #915 — the two sources render differently on purpose. A fact she
            // ASKED for gets a toast she will see immediately; a passively
            // learned one gets the inline chip, because a toast for something
            // she never asked for is noise. Both carry the row id.
            if (event.source === "learn") {
              learnReceiptsRef.current = [...learnReceiptsRef.current, {
                id: event.id,
                category: event.category,
                subject: event.subject,
                key: event.key,
                value: event.value,
                updated: event.updated,
              }];
              // ⚠️ "" — upsertAssistant APPENDS its argument. Passing
              // assistantSoFar here would duplicate the entire reply on screen.
              // The empty chunk is a re-render with the new receipt attached.
              upsertAssistant("");
            } else {
              handleFactReceiptTag(event);
            }
          }
          else if (event?.type === "task_confirm") handleTaskConfirmTag(event, uid);
          else if (event?.type === "task_receipt") handleTaskReceiptTag(event);
          else if (event?.type === "complete_confirm") handleCompleteConfirmTag(event, uid);
          else if (event?.type === "reschedule_confirm") handleRescheduleConfirmTag(event, uid);
          // #1452 — kin-chat wrote a night or committed the week; the bar follows.
          else if (event?.type === "meal_plan_night_updated") {
            setMealPlanCard((prev) => reduceMealPlanCard(prev, { type: "night_updated", runId: event.runId, day: event.day, mealType: event.mealType, mealName: event.mealName, removed: event.removed }));
          } else if (event?.type === "meal_plan_committed") {
            setMealPlanCard((prev) => reduceMealPlanCard(prev, { type: "committed", runId: event.runId }));
          }
        },
        onDone: async () => {
          abortControllerRef.current = null;
          if (imagePromise) {
            const images = await imagePromise;
            if (images.length > 0) {
              upsertAssistant("", images);
            }
          }
          dispatch({ type: "RECEIVE_DONE" });

          // #1900 — open Routines from the FINISHED reply.
          //
          // ⚠️ READS `assistantSoFar`, NOT the displayed text. The display has
          // had both the marker and the draft stripped out of it by
          // upsertAssistant; the raw accumulation is the only place the payload
          // still exists at this point.
          //
          // ⚠️ A DRAFT THAT DOES NOT PARSE OPENS THE EMPTY EDITOR AND SAYS
          // NOTHING. The issue is explicit about that, and it is the right
          // lean: she asked for the tool, so the tool opens. An error toast
          // about a block she was never shown would be Kin reporting its own
          // plumbing, and a refusal to open at all would read as the yes she
          // just gave doing nothing.
          if (assistantSoFar.includes(ROUTINES_TAG)) {
            // ⚠️ ALWAYS AN OBJECT when the marker fired, even if the draft did
            // not parse. That is what tells RoutinesTool to open the editor
            // blank rather than falling through to the list.
            setRoutinesHandoff({ draft: parseRoutineDraft(assistantSoFar) });
            setTimeout(() => setShowRoutines(true), 500);
          }

          // ---- Phase 3: persist assistant message + bump conversation ----
          // Strip magic tags so replayed history doesn't re-trigger modals.
          if (convId && persistUserId) {
            // #1434 — ALL FOUR modal markers, not the two this used to list.
            // GIFT_TAG and SUMMER_TAG were declared above and never stripped
            // here, so they were persisted verbatim and rendered as literal
            // text on reload. stripModalTags iterates the one declaration list,
            // so a fifth marker cannot be added without this site getting it.
            // #1900 — stripRoutineDraft as well, for the same reason the modal
            // markers are stripped: a persisted [ROUTINE_DRAFT] block renders as
            // literal JSON in her chat on any reload, and it is the one tag here
            // whose body is a payload rather than a marker.
            const finalAssistantText = stripRoutineDraft(stripModalTags(assistantSoFar))
              .replace(/\[LEARN\][\s\S]*?\[\/LEARN\]/gi, "")
              .trimEnd();
            const convIdForAssistant = convId;
            if (finalAssistantText) {
              // #1882 — mint the row id client-side so the message on screen can
              // be addressed without a round-trip, and attach it NOW.
              //
              // ⚠️ THE ATTACH CANNOT WAIT FOR THE WRITE. `RECEIVE_DONE` fired
              // just above, so she can already send again — and a send appends
              // a user message plus a fresh empty assistant bubble. "The last
              // assistant message" means this reply only at this instant; a
              // round-trip later it may mean a different one. So: attach here,
              // and take the id back off below if the row cannot be proved
              // (house rule 16). An id is removed from a message that is on her
              // screen either way — never left pointing at a row that is not
              // there.
              //
              // ⚠️ STILL AN `insert`, NOT AN `upsert`, and that is a decision
              // rather than an omission. The USER message upserts (:1650)
              // because `saveMessageWithRetry` can fire the identical write up
              // to three times and a duplicate line is permanent —
              // `from("messages")` has no DELETE anywhere in src/. This path has
              // exactly one attempt, so there is no second write for an upsert
              // to collapse, and `ON CONFLICT DO UPDATE` would only convert a
              // (practically impossible) id collision from a loud PK error into
              // a silent overwrite. If this path ever gains a retry budget — it
              // arguably should, a dropped save loses Kin's reply from history
              // — that change flips this to an upsert in the same commit, for
              // the reason stated at :1650 and not by analogy to it.
              const assistantMessageId = crypto.randomUUID();
              dispatch({ type: "ATTACH_ASSISTANT_ID", id: assistantMessageId });
              void (async () => {
                try {
                  const { data, error } = await supabase
                    .from("messages")
                    .insert({
                      id: assistantMessageId,
                      conversation_id: convIdForAssistant,
                      user_id: persistUserId,
                      role: "assistant",
                      content: finalAssistantText,
                    })
                    // House rule 16 — an RLS refusal answers 200 with no error
                    // and no rows, so the bare insert could not tell a save from
                    // a silent refusal. Asking for the row back is what makes
                    // the id real rather than merely minted.
                    .select("id");
                  if (error || !data?.length) {
                    dispatch({ type: "CLEAR_MESSAGE_ID", id: assistantMessageId });
                    console.error("[chat-persist] Failed to save assistant message:", error);
                  } else {
                    console.log("[chat-persist] saved assistant message", convIdForAssistant);
                  }
                } catch (err) {
                  dispatch({ type: "CLEAR_MESSAGE_ID", id: assistantMessageId });
                  console.error("[chat-persist] Failed to save assistant message:", err);
                }
              })();
            }
            void (async () => {
              try {
                const nowIso = new Date().toISOString();
                const { error } = await supabase
                  .from("conversations")
                  .update({ last_message_at: nowIso, updated_at: nowIso })
                  .eq("id", convIdForAssistant);
                if (error) console.error("[chat-persist] Failed to bump conversation:", error);
                else console.log("[chat-persist] bumped conversation", convIdForAssistant);
              } catch (err) {
                console.error("[chat-persist] Failed to bump conversation:", err);
              }
            })();

            // Phase 5: one-shot auto-title generation for the very first
            // user→assistant exchange of a brand-new conversation. Calls
            // the title_only branch on kin-chat (free, doesn't count
            // against limits). Fully fire-and-forget; any failure leaves
            // title NULL and the drawer shows the italic first-message
            // fallback. Single-fire is guaranteed by flipping the ref
            // BEFORE the network call.
            if (isFirstExchangeRef.current && finalAssistantText) {
              isFirstExchangeRef.current = false;
              void (async () => {
                console.log("[chat-history] firing title generation");
                try {
                  // #1252 — the fetch, the response-shape check and the single
                  // retry live in src/lib/chatTitle.ts. It used to parse the
                  // body blind, which turned a response that belonged to a
                  // DIFFERENT request into a SyntaxError that read like a
                  // kin-chat bug. See that file for why a fall-through was
                  // ruled out as the mechanism.
                  const outcome = await requestChatTitle(
                    session?.access_token,
                    displayText,
                    finalAssistantText
                  );
                  if (!outcome.ok) {
                    // #1709 — a dropped fetch is not an application error. She
                    // loses nothing; the chat keeps its default title and the
                    // drawer shows the italic first-message fallback.
                    //
                    // The check is on `detail`, not on an error object, and
                    // that is forced rather than chosen: requestChatTitle
                    // CATCHES the throw and flattens it (chatTitle.ts:179-183),
                    // so what arrives here is { failure: "threw", detail:
                    // "Load failed" } — a string. isAuthNetworkError keys off
                    // `name`, which no longer exists by this point, so it would
                    // return false on every one of these. Same wording, shared
                    // from one regex; see transportFailure.ts for the three
                    // shapes and which detector owns each.
                    if (
                      outcome.failure === "threw" &&
                      isTransportFailureMessage(outcome.detail)
                    ) {
                      console.debug(
                        "[chat-history] title generate dropped by the network, not reported",
                        outcome.detail
                      );
                      return;
                    }
                    // Everything else is a real answer — an HTTP error, an
                    // empty title, a content-type mismatch (#1252) — and still
                    // reports, via captureConsoleIntegration as before.
                    console.error(
                      `[chat-history] Failed to generate title: ${outcome.failure}`,
                      outcome.detail ?? ""
                    );
                    return;
                  }
                  const title = outcome.title as string;
                  // #1709 — same transport retry the conversation insert uses
                  // (#1530). `.select("id").single()` is required at THIS call
                  // site, not in the helper: its success test is
                  // `!error && data?.id` (createConversationWithRetry.ts:126)
                  // and a bare .update().eq() returns no rows, so without the
                  // select every save would read as a failure. The helper is
                  // deliberately untouched — it is merged and mutation-covered.
                  const saved = await createConversationWithRetry(() =>
                    supabase
                      .from("conversations")
                      .update({ title })
                      .eq("id", convIdForAssistant)
                      .select("id")
                      .single(),
                  );
                  if (saved.id) {
                    console.log("[chat-history] generated title:", title);
                  } else if (saved.lastStatus === TRANSPORT_STATUS) {
                    // Retries exhausted and every attempt was a dropped
                    // connection. PostgREST sets status 0 in exactly one place
                    // — the catch for a rejected fetch — so this cannot
                    // swallow an RLS refusal or a constraint violation, both
                    // of which carry a real HTTP status and fall through.
                    console.debug(
                      "[chat-history] title save dropped by the network, not reported",
                      { attempts: saved.attempts }
                    );
                  } else {
                    // reportError, not a bare console.error: it sets the scope
                    // BEFORE logging, so the event captureConsoleIntegration
                    // synthesises carries these tags too. The bare version
                    // produced an untagged event (#1255).
                    reportError("[chat-history] Failed to save title", saved.lastError, {
                      tags: { step: "chat-title-save" },
                      extra: { attempts: saved.attempts, status: saved.lastStatus },
                    });
                  }
                } catch (err) {
                  // Defensive: requestChatTitle catches its own throws, so this
                  // is reached only by an unexpected one. Here the error object
                  // is still INTACT — it has a `name` — so isAuthNetworkError
                  // is the right detector for this shape, and reusing it beats
                  // re-testing the message. (Shape 1 vs shape 3 in
                  // transportFailure.ts; the two branches of #1709 task 4.)
                  if (isAuthNetworkError(err)) {
                    console.debug(
                      "[chat-history] title generate threw a transport error, not reported",
                      err
                    );
                    return;
                  }
                  console.error("[chat-history] Failed to generate title:", err);
                }
              })();
            }
          }
        },
      });

      if (result.status === "expected_error") {
        abortControllerRef.current = null;
        const errorMsg: Message = {
          role: "assistant",
          content:
            result.code === "LIMIT_REACHED"
              ? LIMIT_MESSAGE
              : result.message || "Something glitched on my end. Try sending that again. 💜",
        };
        // Atomic: clean up empty bubble, append error, clear loading — one render
        dispatch({ type: "ERROR", errorMsg });

        if (result.code === "LIMIT_REACHED" && result.limitReached) {
          markLimitReached();
        }
        return;
      }
    } catch (e: any) {
      abortControllerRef.current = null;
      if (e.name === "AbortError") {
        // User stopped generation — clean up empty assistant bubble + clear loading in one render
        dispatch({ type: "ABORT_CLEANUP" });
        return;
      }
      // Unknown/network error — friendly chat bubble + clear loading in one render
      const errorMsg: Message = {
        role: "assistant",
        content: "Something glitched on my end. Try sending that again. 💜",
      };
      dispatch({ type: "ERROR", errorMsg });
    }
  }, [messages, effectiveLimitReached, markLimitReached, user, handleSignupSheet, endSignupFlow]);

  const handleStop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  // Voice V2 transcript persistence — same conversations/messages tables and
  // insert shape sendMessage's own persistence uses (Phase 3, above), so
  // voice transcripts land in the same thread. Not calling into sendMessage
  // itself: that would re-invoke kin-chat and streaming, which voice mode
  // doesn't need (the model's reply already came back over the data channel).
  const persistChatMessage = useCallback(async (role: "user" | "assistant", content: string) => {
    const persistUserId = user?.id ?? null;
    if (!persistUserId) return;

    let convId: string | null = conversationIdRef.current;
    if (!convId) {
      try {
        const { data, error } = await supabase
          .from("conversations")
          .insert({ user_id: persistUserId })
          .select("id")
          .single();
        if (error) {
          console.error("[voice-persist] Failed to create conversation:", error);
        } else if (data?.id) {
          convId = data.id;
          conversationIdRef.current = convId;
          isFirstExchangeRef.current = true;
        }
      } catch (err) {
        console.error("[voice-persist] Failed to create conversation:", err);
      }
    }
    if (!convId) return;

    try {
      const { error } = await supabase.from("messages").insert({
        conversation_id: convId,
        user_id: persistUserId,
        role,
        content,
      });
      if (error) console.error("[voice-persist] Failed to save message:", error);
    } catch (err) {
      console.error("[voice-persist] Failed to save message:", err);
    }

    const nowIso = new Date().toISOString();
    try {
      await supabase
        .from("conversations")
        .update({ last_message_at: nowIso, updated_at: nowIso })
        .eq("id", convId);
    } catch (err) {
      console.error("[voice-persist] Failed to bump conversation:", err);
    }
  }, [user]);

  // Appends immediately to the visible thread AND persists — voice
  // transcripts arrive as one complete utterance (no streaming), so both
  // happen together rather than the local-echo-then-background-save split
  // sendMessage uses for typed input.
  const handleVoiceTranscript = useCallback((role: "user" | "assistant", content: string) => {
    if (!content.trim()) return;
    dispatch({ type: "APPEND_MESSAGE", msg: { role, content } });
    void persistChatMessage(role, content);
  }, [persistChatMessage]);

  // Voice V2 real conversation mode (WebRTC) — drives the ✦ star.
  const realtimeVoice = useRealtimeVoice({ onTranscript: handleVoiceTranscript });

  const resetChat = () => {
    // Leaving the chat leaves the sign-up flow too (Copilot on #1988): no
    // retry routing into the new chat, and any in-flight answer is dropped.
    endSignupFlow();
    // Atomic: clear messages + isLoading in one render
    dispatch({ type: "RESET" });
    // Phase 3: starting a fresh chat must also drop the conversation id so
    // the next send creates a new conversations row instead of silently
    // appending to the previous one. Ref write — no re-render.
    conversationIdRef.current = null;
    // Clear local limit flag so the user can attempt to send again.
    // The server is the source of truth and will reject with 429 if still limited.
    clearLimitFlag();
  };

  // Phase 4: load the user's recent conversations into the drawer.
  // Two queries to avoid N+1 (PostgREST can't do correlated subqueries cleanly).
  // RLS already scopes to auth.uid().
  const loadDrawerConversations = useCallback(async (opts?: { silent?: boolean }) => {
    // `silent` refreshes the list in place after a delete. Without it the
    // skeleton flashes for a one-row change, and — worse — the free tier's
    // 6th chat could never slide up into the 5 visible slots.
    if (!opts?.silent) setDrawerLoading(true);
    try {
      // Phase 5: Pro tier sees up to 50 most recent; Free tier sees 5.
      // We fetch limit+1 on Free so we can derive `hasMore` without a
      // second count(*) round-trip, then slice down to 5 for display.
      const fetchLimit = isProTier ? 50 : 6;
      const { data: convsRaw, error: convErr } = await supabase
        .from("conversations")
        .select("id,title,last_message_at")
        .is("archived_at", null)
        .order("last_message_at", { ascending: false })
        .limit(fetchLimit);
      if (convErr) throw convErr;
      const hasMore = !isProTier && (convsRaw?.length ?? 0) > 5;
      const convs = isProTier ? (convsRaw ?? []) : (convsRaw ?? []).slice(0, 5);
      const ids = convs.map((c) => c.id);
      let firstUserByConv = new Map<string, string>();
      let lastAsstByConv = new Map<string, string>();
      if (ids.length > 0) {
        const { data: msgs, error: msgErr } = await supabase
          .from("messages")
          .select("conversation_id,role,content,created_at")
          .in("conversation_id", ids)
          .order("created_at", { ascending: true });
        if (msgErr) throw msgErr;
        for (const m of msgs ?? []) {
          if (m.role === "user" && !firstUserByConv.has(m.conversation_id)) {
            firstUserByConv.set(m.conversation_id, m.content);
          }
          if (m.role === "assistant") {
            // ascending order → last write wins = most recent assistant
            lastAsstByConv.set(m.conversation_id, m.content);
          }
        }
      }
      const enriched: DrawerConversation[] = convs.map((c) => ({
        id: c.id,
        title: c.title,
        last_message_at: c.last_message_at,
        preview: lastAsstByConv.get(c.id) ?? null,
        first_user_message: firstUserByConv.get(c.id) ?? null,
      }));
      setConversations(enriched);
      setDrawerShowUpgradeFooter(hasMore);
    } catch (err) {
      console.error("[chat-history] Failed to load conversations", err);
      // Only an OPENING load may clear the list. A silent post-delete refresh
      // that fails must leave what is on screen alone: blanking it would tell
      // her every chat is gone when the network merely blinked.
      if (!opts?.silent) {
        setConversations([]);
        setDrawerShowUpgradeFooter(false);
      }
    } finally {
      setDrawerLoading(false);
    }
  }, [isProTier]);

  const openDrawer = useCallback(async () => {
    setDrawerOpen(true);
    await loadDrawerConversations();
  }, [loadDrawerConversations]);

  const handleSelectConversation = useCallback(async (id: string) => {
    setDrawerOpen(false);
    // Switching conversations ends the sign-up flow (Copilot on #1988).
    endSignupFlow();
    try {
      // #1882 / #1877 — the column list, the row→Message mapping and the
      // pre-migration 42703 fallback are ONE unit, kept together in
      // src/lib/chatMessageIdentity.ts and tested against a fake client there.
      // This used to be an inline `.select("role,content,created_at")` feeding
      // an inline two-field object literal, which is the explicit-field-list
      // trap: a column added to one half and not the other is dropped in total
      // silence. Nothing is spelled out here any more, so nothing can drift.
      const mapped: Message[] = await readConversationMessages(supabase as never, id);
      // Atomic: replace messages + clear loading in one render
      dispatch({ type: "LOAD", messages: mapped });
      // Ref write — no re-render
      conversationIdRef.current = id;
      // Force re-pin to bottom even if user had scrolled up in prior view.
      requestAnimationFrame(() => chatMessagesRef.current?.resetScrollLock());
    } catch (err) {
      console.error("[chat-history] Failed to load messages", err);
    }
  }, [endSignupFlow]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    // Captured BEFORE the optimistic removal so a failure can put the row
    // back exactly where it was. Rollback must not depend on the network —
    // if it did, losing connectivity would both fail the delete and strand
    // the row as deleted on screen.
    const removedIndex = conversations.findIndex((c) => c.id === id);
    const removedRow = removedIndex === -1 ? null : conversations[removedIndex];

    setConversations((prev) => prev.filter((c) => c.id !== id));
    try {
      await deleteConversation(supabase, id);

      // If she deleted the thread she is currently reading, the ref still
      // points at a row that no longer exists. The next send would take the
      // `if (!convId)` FALSE branch in persistChatMessage, skip creating a
      // conversation, and insert a message against a dangling
      // conversation_id — an FK violation on a path that deliberately
      // swallows its errors, so the exchange would vanish with no warning.
      if (conversationIdRef.current === id) {
        // Abort FIRST. A reply may still be streaming into this thread, and
        // resetChat alone neither stops it nor invalidates its callbacks:
        // onDelta would keep appending the rest of the answer into the
        // freshly-cleared transcript, and onDone would try to persist it
        // against the deleted convId it captured. Aborting makes the stream
        // reader throw, so the loop exits and onDone is never reached.
        abortControllerRef.current?.abort();
        resetChat();
      }

      toast.success("Chat deleted.");
      // Backfills the free tier's 6th chat into the slot this one freed.
      await loadDrawerConversations({ silent: true });
    } catch (err) {
      console.error("[chat-history] Failed to delete conversation", err);
      // Local restore, no refetch: nothing was deleted, so the list on screen
      // plus this row IS the truth, and a second request could fail too.
      if (removedRow) {
        setConversations((prev) =>
          prev.some((c) => c.id === id)
            ? prev
            : [...prev.slice(0, removedIndex), removedRow, ...prev.slice(removedIndex)],
        );
      }
      toast.error("Couldn't delete that chat. Try again?");
    }
  }, [conversations, loadDrawerConversations]);

  const handleImageSelect = useCallback((image: ImageResult | null) => {
    if (image) {
      sendMessage(`Tell me more about this look — I love the "${image.title}" photo!`);
    }
  }, [sendMessage]);

  const handleAskAboutPhoto = useCallback(() => {
    chatInputRef.current?.focus();
  }, []);

  const handleMomSelectorComplete = async () => {
    await refetchMomTypes();
    setShowMomSelector(false);
    // After mom types saved, refresh kid status — gate effect will open KidsCrewSetup if needed.
    refetchPreferences();
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: "var(--vv-height, 100dvh)", backgroundColor: "#F0F0FF" }}>
      <KBDebugStrip />
      {activeTab === "chat" && (
        <>
          <ChatHeader
            onLogoClick={resetChat}
            leadingSlot={
              <button
                onClick={openDrawer}
                aria-label="Past chats"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "rgba(80, 80, 200, 0.12)",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                <MessagesSquare size={18} color="#5050C8" />
              </button>
            }
          />
          {/* --vv-height is written by the useLayoutEffect above from the Median
              keyboard listener (clientHeight - stableOffset). It is NOT from
              raw visualViewport — Median WKWebView VV values are inflated/unreliable.
              ChatInput sits at the column bottom as flex-shrink-0 and tracks the
              keyboard top without any position:fixed bottom math.
              BottomNavBar is still position:fixed; paddingBottom clears it when
              the keyboard is closed (nav is hidden when keyboard is open). */}
          <div
            style={{
              height: "var(--vv-height, 100dvh)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              paddingBottom: keyboardOpen
                ? 0
                : `calc(${NAV_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
              // Animate with the iOS keyboard curve so the column glides in/out
              // with the keyboard instead of snapping after it.
              transition: "height 250ms cubic-bezier(0.38, 0.7, 0.125, 1)",
            }}
          >
            <main
              className="overflow-y-auto flex-1 flex flex-col"
              style={{
                paddingTop: "calc(env(safe-area-inset-top, 0px) + 72px)",
              }}
            >
              {hasMessages ? (
                <ChatMessages
                  ref={chatMessagesRef}
                  messages={messages}
                  isLoading={isLoading}
                  selectedImageUrl={null}
                  onImageSelect={handleImageSelect}
                  onAskAboutPhoto={handleAskAboutPhoto}
                  onOpenRenoTracker={() => setShowRenoTracker(true)}
                  onSendMessage={sendMessage}
                  onSignupRowsChosen={handleSignupRowsChosen}
                  keyboardOpen={keyboardOpen}
                />
              ) : (
                <WelcomeScreen
                  onSendMessage={sendMessage}
                  momTypes={momTypes}
                  momTypesLoading={momTypesLoading}
                  isPro={isProTier}
                  onActivateVoiceMode={() => realtimeVoice.connect()}
                  onSignupSheet={handleSignupSheet}
                />
              )}
            </main>

            {/* #1452 — Save & commit for the meal plan this thread is about.
                A flex child of the column, never position:fixed: the column
                owns the keyboard math (see the note above). */}
            <MealPlanCommitBar
              state={mealPlanCard}
              committing={mealPlanCommitting}
              onCommit={() => void handleCommitMealPlanFromChat()}
              onOpenPlan={() => setActiveTab("tasks")}
            />

            {/* Single ChatInput instance — always mounted while on chat tab so
                the welcome→chat transition does not unmount/remount the input
                mid-tap (was causing first-tap-drops on the send button).
                flex-shrink-0 keeps it pinned at the column bottom. */}
            <ChatInput
              ref={chatInputRef}
              onSend={sendMessage}
              disabled={isLoading || effectiveLimitReached}
              isStreaming={isLoading}
              onStop={handleStop}
              showNewChat={hasMessages}
              onNewChat={resetChat}
              limitReached={effectiveLimitReached}
              keyboardOpen={keyboardOpen}
              isProUser={isProTier}
              voiceModeState={realtimeVoice.state}
              onVoiceModeToggle={realtimeVoice.toggle}
              onDeactivateVoiceMode={realtimeVoice.end}
            />
          </div>

          <ConversationsDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSelectConversation={handleSelectConversation}
            onDeleteConversation={handleDeleteConversation}
            conversations={conversations}
            loading={drawerLoading}
            showUpgradeFooter={drawerShowUpgradeFooter}
          />
        </>
      )}

      {activeTab === "tools" && (
        <Suspense fallback={<TabLoader />}>
          {/* ⚠️ NO trackFeature CALLS HERE, and their removal is the #1900 fix.
              Every one of these handlers used to call trackFeature with the
              same feature_name ToolsTab.openTool already writes, so each tile
              tap inserted TWO feature_usage rows. That was invisible while it
              was uniform — "Your go-tos" ranks on raw row count, so doubling
              everything changed no order — but a new tile wired the honest way
              would have needed twice the opens to rank level with the rest.
              ToolsTab.openTool is the single tracking site; its own comment
              already said so ("one place a tile is opened from, so the
              tracking cannot drift from the gate"). These were #1891
              leftovers. toolsTabSections.test.ts now guards it both ways. */}
          <ToolsTab
            onOpenRenoTracker={() => { setTrackerBudgetId(null); setShowRenoTracker(true); }}
            onOpenGiftTracker={() => setShowGiftTracker(true)}
            onOpenProteinTracker={() => setShowProteinTracker(true)}
            onOpenGardenPlanner={() => setShowGardenPlanner(true)}
            onOpenSummerPlanner={() => setShowSummerPlanner(true)}
            onOpenTripPlanner={() => openTripPlanner()}
            onOpenPartyPlanner={() => setShowPartyPicker(true)}
            onOpenRoutines={() => setShowRoutines(true)}
          />
        </Suspense>
      )}

      {activeTab === "tasks" && tasksTabEnabled && (
        <Suspense fallback={<TabLoader />}>
          <TasksTab
            onNavigateToChat={(message) => {
              setPendingChatMessage(message);
              setActiveTab("chat");
            }}
            // #1450 — "Talk to Kin about this plan": switch AND send, the same
            // handoff the Today tab's onAskKin uses below. Not a second mechanism.
            onAskKin={(context) => {
              setActiveTab("chat");
              setTimeout(() => {
                sendMessage(context);
              }, 200);
            }}
            highlightTaskId={highlightTaskId}
            onHighlightConsumed={() => setHighlightTaskId(null)}
          />
        </Suspense>
      )}


      {activeTab === "today" && (
        <Suspense fallback={<TabLoader />}>
          <TodayTab
            activationKey={todayActivations}
            onAskKin={(context) => {
              setActiveTab("chat");
              setTimeout(() => {
                sendMessage(context);
              }, 200);
            }}
            onOpenTripPlanner={(tripId) => openTripPlanner(tripId)}
          />
        </Suspense>
      )}

      {activeTab === "mykin" && (
        <Suspense fallback={<TabLoader />}>
          <MyKinTab
            activationKey={myKinActivations}
            onOpenBudget={(id) => {
              setTrackerBudgetId(id);
              setActiveTab("tools");
              setTimeout(() => setShowRenoTracker(true), 100);
            }}
            onMakeRecipeGroceryList={(recipe) => {
              setActiveTab("chat");
              setTimeout(() => {
                sendMessage(
                  `Here's a recipe I saved. Generate just the grocery list for this recipe: ${recipe.title}\n\n${recipe.full_text}`
                );
              }, 200);
            }}
          />
        </Suspense>
      )}

      <BottomNavBar
        activeTab={activeTab}
        onTabChange={(tab) => {
          if (tab !== activeTab) {
            trackFeature(`tab_${tab}`);
          }
          // startTransition keeps the CURRENT tab painted while the next
          // one renders (Aug 21, the "weird flash"). Without it, switching
          // unmounts the old tab and then blanks for however long the new
          // tab's first render takes — ~2 frames on desktop, visibly longer
          // on device for a component the size of TasksTab. Marking the
          // switch as a transition lets React prepare the new tab
          // concurrently and swap in ONE commit: no blank, no fallback.
          // The chunk warm-up above makes the wait imperceptible; this makes
          // whatever wait remains invisible.
          startTransition(() => setActiveTab(tab));
        }}
        hidden={keyboardOpen}
      />
      <LazyMount open={showRenoTracker}>
        <RenoBudgetTracker
          open={showRenoTracker}
          onClose={() => { setShowRenoTracker(false); setTrackerBudgetId(null); }}
          initialBudgetId={trackerBudgetId}
        />
      </LazyMount>
      <LazyMount open={showGiftTracker}>
        <GiftTracker
          open={showGiftTracker}
          onClose={() => setShowGiftTracker(false)}
        />
      </LazyMount>
      <LazyMount open={showProteinTracker}>
        <ProteinTracker
          open={showProteinTracker}
          onClose={() => setShowProteinTracker(false)}
        />
      </LazyMount>
      <LazyMount open={showPartyPlanner}>
        <PartyPlannerModal
          open={showPartyPlanner}
          onClose={() => { setShowPartyPlanner(false); setEditParty(null); }}
          onOpenHub={(id) => setHubPartyId(id)}
          editParty={editParty}
          editStep={editStep}
        />
      </LazyMount>
      <LazyMount open={showPartyPicker}>
        <PartyPickerGate
          open={showPartyPicker}
          onClose={() => setShowPartyPicker(false)}
          onPick={(id) => { setShowPartyPicker(false); setHubPartyId(id); }}
          onNew={() => { setShowPartyPicker(false); setShowPartyPlanner(true); }}
        />
      </LazyMount>
      <LazyMount open={!!hubPartyId}>
        <PartyHub
          open={!!hubPartyId}
          partyId={hubPartyId}
          onClose={() => setHubPartyId(null)}
          onEditParty={(p, step) => {
            setHubPartyId(null); setEditParty(p); setEditStep(step); setShowPartyPlanner(true);
          }}
          onDeleted={() => setHubPartyId(null)}
          onOpenBudget={(id) => { setHubPartyId(null); setTrackerBudgetId(id); setShowRenoTracker(true); }}
        />
      </LazyMount>
      <LazyMount open={showGardenPlanner}>
        <GardenPlannerTool
          open={showGardenPlanner}
          onClose={() => setShowGardenPlanner(false)}
        />
      </LazyMount>
      <LazyMount open={showRoutines}>
        <RoutinesTool
          open={showRoutines}
          handoff={routinesHandoff}
          onClose={() => {
            setShowRoutines(false);
            // ⚠️ CLEARED ON CLOSE. A hand-off is one event, not a setting:
            // leaving it set would re-open the editor the next time she reached
            // Routines from the Tools tile, from a conversation days old.
            // RoutinesTool clears its own chat-created draft for the same
            // reason, because LazyMount keeps it mounted and this alone was not
            // enough.
            setRoutinesHandoff(null);
          }}
        />
      </LazyMount>
      <LazyMount open={showSummerPlanner}>
        <SummerPlannerTool
          isOpen={showSummerPlanner}
          onClose={() => setShowSummerPlanner(false)}
        />
      </LazyMount>
      <LazyMount open={showTripPlanner}>
        <TripPlannerTool
          isOpen={showTripPlanner}
          initialTripId={initialTripId}
          // #1887 — cleared on close, so the NEXT open from a tile shows the
          // list rather than reopening whichever trip she last came from.
          onClose={() => { setShowTripPlanner(false); setInitialTripId(null); }}
        />
      </LazyMount>
      {showNameCapture && (
        <NameCaptureModal
          onComplete={() => {
            if (user) localStorage.setItem(`kin_name_capture_shown_${user.id}`, 'true');
            setNameCaptureDismissed(true);
            setShowNameCapture(false);
          }}
        />
      )}
      {showMomSelector && (
        <MomTypeSelector
          onComplete={handleMomSelectorComplete}
          onDismiss={() => {
            if (user) localStorage.setItem(`kin_mom_type_dismissed_${user.id}`, 'true');
            setMomTypeDismissed(true);
            setShowMomSelector(false);
          }}
        />
      )}
      {showKidsCrew && (
        <KidsCrewSetup onComplete={(skipped?: boolean) => { if (skipped && user) localStorage.setItem(`kin_kids_crew_skipped_${user.id}`, 'true'); setShowKidsCrew(false); setKidsCrewDismissed(true); refetchPreferences(); }} />
      )}
      {showWelcomeOnboarding && (
        <WelcomeOnboarding
          onDismiss={() => {
            // Tour finished — refetch onboarding_complete; gate effect opens mom selector next.
            refetchMomTypes();
          }}
        />
      )}
      <KeyboardDiagModalV2 />
      <SavedToast />
    </div>
  );
};

export default Index;
